/**
 * Registry Start — intake flow state machine.
 *
 * Consumes window.BirchRegistry (registry-store.js) — never modifies it —
 * window.BIRCH_REGISTRY_CATEGORIES (emitted by the section from its blocks) and
 * window.BirchDetailPopup (registry-detail-popup.js) for the product sheet.
 * All chrome is pre-rendered in Liquid and toggled with [hidden]; the only
 * HTML built from data are product cards and rail rows, and every dynamic
 * string goes through BirchRegistry.escapeHtml.
 *
 * Exposes ONE thing to the outside, window.BirchRegistryStart.addFromViewMore
 * — CUR-80 Task 9's view-more panel (guided-registry-recs.js) calls it to add
 * a candidate through the exact same BR.API.addItem() path and success-path
 * bookkeeping handleAdd uses, since its cards aren't `.rst-pcard`s and can't
 * drive handleAdd directly. See addFromViewMore's own comment below.
 *
 * Persistence: answers/progress merge-patch onto the registry row via
 * POST /registries/:id/onboarding — debounced 600 ms, coalesced, flushed on
 * pagehide with keepalive. Failures are non-fatal: in-memory state is
 * authoritative for the session and the server catches up on the next write.
 *
 * SCROLL POLICY: this flow parks the window once, on entry — parkFlow(), which
 * brings the setup header to the top of the viewport instantly, at script-eval
 * time, before the registry is even resolved (it is called again at DOM ready
 * and after route(), but those only correct drift and are normally no-ops). The
 * entry scroll must never be perceptible as a scroll: no smooth behavior, and
 * nothing waits on a network round-trip. Every screen and step change after that
 * leaves the scroll position alone (and focuses with preventScroll), so moving
 * forward reads as the panel updating in place rather than as a page
 * navigation. The header is sticky, so it stays put regardless.
 *
 * EXCEPTION: the guided-registry machine (guided-registry-screens.js) replaces
 * its shared shell's contents wholesale on every screen change, not as an
 * incremental step within a panel, so a new screen painted at the old scroll
 * position would open mid-page. Its renderCurrent() parks every NEW screen
 * (parkQuestion: the question lands directly under the sticky theme header and
 * the flow's own sticky top bar), reusing this file's jumpTo() instant-scroll
 * pattern (own copy — this IIFE doesn't expose jumpTo); it does not otherwise
 * touch scroll. parkFlow() stands aside on that screen for the same reason.
 */
(function () {
  'use strict';

  var API_BASE = '/apps/registry/api';
  var PLAN_ENDPOINT = '/apps/ai/registry/guided-plan';
  var MAX_PRIORITIES = 3;
  // CANONICAL page size. The browse-feed edge cache is keyed on
  // cat/sub/limit/offset/sort/exclude — a per-row limit forks that key into a
  // dozen variants and tanks the hit rate (the editor learned this the hard
  // way). Fetch 12, slice client-side.
  var FEED_LIMIT = 12;
  // Backstop for the continuous carousel, not a page size — high enough that
  // scrolling a row genuinely keeps producing new options, low enough that a
  // long session can't grow one row into thousands of nodes.
  var ROW_CARD_CAP = 72;
  var ONBOARDED_KEY = 'birch:reg:onboarded';
  // CUR-179 — why resolveRegistry rejected when "+ New registry" couldn't be
  // created (see createNewRegistry). boot() shows a retry for it.
  var NEW_REGISTRY_FAILED = 'new_registry_failed';

  var BR = window.BirchRegistry;
  if (!BR) return;
  var esc = BR.escapeHtml;

  var CATEGORIES = Array.isArray(window.BIRCH_REGISTRY_CATEGORIES) ? window.BIRCH_REGISTRY_CATEGORIES : [];
  // The "Earn n% back" badge is rendered in Liquid, on Birch cards only — feed
  // cards are other retailers' listings and earn nothing, so nothing here needs
  // window.BIRCH_REWARD_PERCENT.

  // ── state ────────────────────────────────────────────────────────────────
  var state = {
    registry: null,
    onboarding: { v: 1, answers: {} },
    // CUR-151 — set while ONE setup page is open from the guided recap's
    // Change: { from: page }. Continue then asks only what the change left
    // unanswered and returns to the recap; Back returns straight away.
    editSetup: null,
    plan: null,
    planAnswersKey: null,
    screen: '1',
    stepIndex: 0,
    added: {},      // registryItemId → { stepKey, qty }
    addedCount: 0,
    addedKeys: null, // identity strings for feed dedupe (set in boot)
    priorityOrder: [], // tap order, for the oldest-drop cap
    history: [],    // flow-local back stack of location snapshots
    parkDone: false, // entry scroll is settled / the person took over (parkFlow)
    parkY: null,     // where the last park landed, to tell our scroll from theirs
    advanceTimer: null, // pending auto-advance (§7)
  };
  state.addedKeys = Object.create(null);

  var root = document.querySelector('[data-rst-root]');
  if (!root) return;

  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }

  // ── journey-specific flows (§9) ──────────────────────────────────────────
  // Babylist's lesson: the questions themselves change with the path, and a
  // question that cannot have an answer is not asked at all. "Planning ahead"
  // has no date to give, so the timing page is dropped from its flow outright
  // rather than shown and skipped.
  //
  // Adopting and "baby's here" drop the DATE for the same reason: neither has a
  // due date to plan around — one is a timeline nobody controls, the other is a
  // birth date already behind them — and asking for a date we only use to time
  // a countdown makes the flow longer without changing a single recommendation.
  // They still walk page 2, for the one question that DOES change them: `age`,
  // how old the child is when they arrive. It is asked ONLY on those two,
  // because they are the only journeys where the answer can be anything but
  // "newborn" — everyone else is expecting a baby, and asking would be asking a
  // question with one possible answer. `noDate` hides the date field; `age`
  // shows the band; `planning` has neither, so it skips the page outright.
  //
  // 'path' is the fork ("how would you like to build it?"): its own page, and
  // always the last one, so pageAfter() walks straight into it.
  var JOURNEY = {
    expecting: {
      pages: ['1', '2', '3'],
      dateHeading: 'When are they arriving?',
      dateSub: 'This helps us tailor product recommendations to the right stage.',
      dateLabel: 'Estimated due date',
      dateFuture: true,
      ageHeading: 'Which ages should we cover?',
      ageHint: 'They arrive as a newborn either way — this just tells us how far ahead to plan.',
      multiples: 'More than one is on the way',
      firstHeading: 'Is this your first baby?',
    },
    surrogacy: {
      pages: ['1', '2', '3'],
      dateHeading: 'When are they arriving?',
      dateSub: 'Your surrogate’s due date, so we can time our recommendations around it.',
      dateLabel: 'Estimated due date',
      dateFuture: true,
      multiples: 'More than one is on the way',
      firstHeading: 'Is this your first baby?',
    },
    adopting: {
      // No date asked: an adoption timeline firms up on its own schedule. The
      // age band is asked instead — a child can come home at any age, and that
      // age is what the plan is built around.
      pages: ['1', '2', '3'],
      noDate: true,
      age: true,
      dateHeading: 'How old will they be when they arrive?',
      dateSub: 'A rough band is plenty — it decides the sizes and stages we show you.',
      ageHeading: 'Age when they come home',
      multiples: 'More than one child is joining us',
      firstHeading: 'Is this your first child?',
    },
    arrived: {
      // No date asked: the baby is here, so there is nothing to count down to —
      // but how old they are now is exactly what we need.
      pages: ['1', '2', '3'],
      noDate: true,
      age: true,
      dateHeading: 'How old are they now?',
      dateSub: 'Their age is what decides which sizes and stages we show you.',
      ageHeading: 'How old they are today',
      multiples: 'More than one baby',
      firstHeading: 'Is this your first baby?',
    },
    planning: {
      // The only journey that skips page 2 outright: there is no date to give
      // and no child whose age we could ask for.
      pages: ['1', '3'],
      multiples: 'We’re hoping for more than one',
      firstHeading: 'Would this be your first baby?',
    },
  };
  // Page 1 opens with this already chosen (the tile ships with rst-chip--on in
  // Liquid so it's selected on first paint). It is a real answer, not a
  // placeholder: pressing Continue carries it forward exactly as a tap would,
  // and the tiles are sticky, so re-tapping it just re-confirms and advances.
  var DEFAULT_JOURNEY = 'expecting';
  var DEFAULT_FLOW = JOURNEY[DEFAULT_JOURNEY];
  // The fork page. Last in every flow, so "the end of the questions" and "the
  // fork" are the same place no matter which journey you walked.
  var FORK = 'path';
  // The conditional page: "which of these do you already have?", asked only
  // after "No, not my first". It is not in any journey's base list because it
  // is a question with no meaning for anyone else — it is spliced in front of
  // the fork the moment the answer calls for it, and disappears again if the
  // answer changes. See BIG_TICKET below for what it collects and why it is
  // asked HERE rather than at the end of the walkthrough.
  var OWN = 'own';

  // The durables a repeat parent carries over from the last baby. Mirrors
  // BIG_TICKET in the worker's guided/plan.ts. The test is "does it survive one
  // child and get used again", not "is it expensive" — a sound machine is as
  // certainly still in the cupboard as the crib is. Car Seats are deliberately
  // absent from both (they carry an expiry date, and a second child in the car
  // needs a second seat).
  var BIG_TICKET = [
    { key: 'crib', label: 'crib', cat: 'Nursery', sub: 'Cribs' },
    { key: 'bassinet', label: 'bassinet', cat: 'Nursery', sub: 'Bassinets' },
    { key: 'stroller', label: 'stroller', cat: 'Gear', sub: 'Strollers' },
    { key: 'monitor', label: 'baby monitor', cat: 'Tech', sub: 'Smart Monitors' },
    { key: 'sound_machine', label: 'sound machine', cat: 'Nursery', sub: 'Sound Machines' },
    { key: 'bouncer', label: 'bouncer', cat: 'Nursery', sub: 'Bouncers & Rockers' },
    { key: 'travel_crib', label: 'travel crib', cat: 'Nursery', sub: 'Play Yards & Travel Cribs' },
    { key: 'high_chair', label: 'high chair', cat: 'Feeding', sub: 'High Chairs' },
  ];
  // Mirror of TWINS_KEEP_DEFERRED in guided/plan.ts — the dividing line is
  // per-room vs per-baby. One monitor and one sound machine cover a shared room;
  // a second sleep surface, a double stroller, a second bouncer, a second travel
  // crib and a second high chair are all genuine second purchases.
  var TWINS_KEEP_DEFERRED = ['monitor', 'sound_machine'];

  // ── how many babies (§ "More than one is on the way") ────────────────────
  // The checkbox says THAT there is more than one; `multiplesCount` says how
  // many. 2 is the default because it is overwhelmingly the answer, so the
  // usual case is still one tap: the select appears already reading "Twins".
  // 4 is the top of the scale and means "four or more" — the difference
  // between quadruplets and quintuplets changes nothing we do with the number,
  // and the quantity ceilings downstream cap there anyway.
  var MULTIPLES_MIN = 2;
  var MULTIPLES_MAX = 4;
  var MULTIPLES_COUNTS = [2, 3, 4];
  // Numerals for sentences that count the family ("with three on the way").
  var MULTIPLES_NUMERAL = { 2: 'two', 3: 'three', 4: 'four or more' };

  // ── how many of a row a family usually wants ─────────────────────────────
  // Mirrors QTY_BY_CATEGORY / TWINS_DOUBLE in the AI builder's
  // build/quantities.ts, re-keyed onto the THEME's subcategory labels (the
  // vocabulary this page actually renders). Two lists, because the two behave
  // differently when there is more than one baby:
  //
  //   PER_BABY   the suggestion is this many PER CHILD — a second baby needs a
  //              second sleep surface and their own bottles, and each child
  //              legally needs their own car seat.
  //   SHARED     one household's worth however many babies arrive — a tin of
  //              formula and a changing pad do not double.
  //
  // Strollers are deliberately in NEITHER: two babies want ONE double or tandem
  // stroller, not two strollers, which is the whole reason the twins carousel
  // leads with doubles (see promoteMultiRider). Anything unlisted suggests 1,
  // which renders as no suggestion at all.
  //
  // The durables on this list are NOT in quantities.ts — the AI builder holds
  // every durable at 1 on purpose (see the critic's QUANTITIES rule). The two
  // are answering different questions: the builder is choosing ONE product for
  // a slot, while this number is "how many go on your registry", and two babies
  // sleeping in one crib is not a thing. `Crib Mattresses` is here for the same
  // reason `Cribs` is, and must stay in step with it: a mattress is bought per
  // crib, so suggesting two cribs and one mattress is advice that contradicts
  // itself on the same step.
  var PER_BABY_SUGGEST = {
    'Cribs': 1, 'Crib Mattresses': 1, 'Bassinets': 1, 'Crib Sheets': 3,
    'Bouncers & Rockers': 1, 'Play Yards & Travel Cribs': 1, 'Car Seats': 1, 'High Chairs': 1,
    'Bottles': 4, 'Bibs': 2, 'Pacifiers': 3, 'Sleep Sacks': 2,
    'Bodysuits': 2, 'Diapers': 2, 'Wipes': 3,
  };
  var SHARED_SUGGEST = { 'Formula': 2, 'Changing Pads': 2 };
  // Ceiling on anything we say out loud. Mirrors QTY_CEILING's job in
  // quantities.ts: past a point a number stops being advice and starts being a
  // shopping list nobody asked for ("about 12 packs of wipes").
  var SUGGEST_CEILING = 8;
  // Sub label → the big-ticket key that owns it, so the suggestion can subtract
  // what the family already told us they have.
  var SUB_BIG_TICKET_KEY = BIG_TICKET.reduce(function (m, b) { m[b.sub] = b.key; return m; }, {});
  // Subs the `own` page never names, but which arrive WITH something it does. A
  // crib they already own is a crib with a mattress in it, so both rows have to
  // subtract the same one — otherwise the same step advises two cribs and three
  // mattresses. Sheets and covers are deliberately absent: those are replaced,
  // not carried over.
  var PAIRED_BIG_TICKET_KEY = { 'Crib Mattresses': 'crib' };

  /** Do they already own this durable? True only when they were ASKED (the
   *  `own` page runs on "not my first") and left the tile marked "have".
   *  `bigTicketWanted` is the complement — what they still need. */
  function ownsBigTicket(sub) {
    var a = state.onboarding.answers || {};
    var key = SUB_BIG_TICKET_KEY[sub] || PAIRED_BIG_TICKET_KEY[sub];
    if (!key || a.firstBaby !== false) return false;
    var wanted = a.bigTicketWanted;
    // No answer at all = the pre-marked default, which is "have" for everything
    // except the twins carve-out. Same gate as deferredFor.
    if (!Array.isArray(wanted)) {
      return a.multiples === true ? TWINS_KEEP_DEFERRED.indexOf(key) !== -1 : true;
    }
    return wanted.indexOf(key) === -1;
  }

  /**
   * How many of this row we suggest — the number the row's one short line says,
   * and nothing else: every Add adds ONE, always. 1 means "no suggestion".
   *
   * The subtraction at the end is the case a twins family kept hitting: they
   * tell us on the `own` page that they have a crib, and they also tell us two
   * babies are coming. Owning one crib does not cover two children, so the row
   * stays (see deferredFor) — but suggesting "2" would be telling someone to
   * buy a crib they already own. Suggest the SHORTFALL instead.
   */
  function suggestedQty(sub) {
    var perBaby = PER_BABY_SUGGEST[sub];
    var n = perBaby != null ? perBaby * babyCount() : (SHARED_SUGGEST[sub] || 1);
    if (perBaby != null && ownsBigTicket(sub)) n -= 1;
    return Math.max(1, Math.min(SUGGEST_CEILING, n));
  }

  // The pill's wording for the family. Sits inside a chip beside a row title,
  // where "four or more" is a label rather than a full sentence.
  var SUGGEST_NOUN = { 2: 'twins', 3: 'triplets', 4: 'four or more' };

  /** The row's one line. A chip, so it carries only the three things that have
   *  to be there: that this is a SUGGESTION and not a count of anything, the
   *  number, and — when it is the number of babies driving it — why. "Suggested:
   *  8 for twins", "Suggested: 2". Null when there is nothing worth saying. */
  function suggestText(sub) {
    var n = suggestedQty(sub);
    if (n < 2) return null;
    var babies = babyCount();
    return 'Suggested: ' + n + (babies > 1 ? ' for ' + (SUGGEST_NOUN[babies] || 'more than one') : '');
  }

  /** How many babies this registry is being built for — 1 unless they said
   *  otherwise. Every quantity, default and sentence downstream reads this
   *  rather than `multiples` directly, so triplets are never quietly served
   *  the twins answer. */
  function babyCount() {
    var a = state.onboarding.answers || {};
    if (a.multiples !== true) return 1;
    var n = parseInt(a.multiplesCount, 10);
    return n >= MULTIPLES_MIN && n <= MULTIPLES_MAX ? n : MULTIPLES_MIN;
  }

  function flow() {
    var j = (state.onboarding.answers || {}).journey;
    return (j && JOURNEY[j]) || DEFAULT_FLOW;
  }
  function pages() {
    var list = flow().pages;
    if ((state.onboarding.answers || {}).firstBaby !== false) return list;
    var out = list.slice();
    var i = out.indexOf(FORK);
    out.splice(i === -1 ? out.length : i, 0, OWN);
    return out;
  }
  function isQuestionPage(name) { return pages().indexOf(String(name)) !== -1; }

  /** The page that follows `name` in THIS journey's flow, or null at the end. */
  function pageAfter(name) {
    var list = pages();
    var i = list.indexOf(String(name));
    return i === -1 || i === list.length - 1 ? null : list[i + 1];
  }

  // ── CUR-151: editing setup answers from the guided recap ─────────────────
  /** The journey facts guided-setup-edit.js needs, from THIS journey's config. */
  function setupCfg() {
    var f = flow();
    return {
      pages: pages(),
      noDate: !!f.noDate,
      age: !!f.age,
      dateLabel: f.dateLabel || 'Due date',
      ageLabel: f.ageHeading || 'Age',
      bigTicket: BIG_TICKET.map(function (b) { return { key: b.key, label: b.label }; }),
    };
  }

  /** Open one setup page from the recap. Every tap on it is saved as it
   *  happens (persist), so there is nothing to confirm or cancel — Continue
   *  asks whatever the change left unanswered, then returns to the recap. */
  function editSetup(page) {
    page = String(page);
    if (page === FORK || (!isQuestionPage(page) && page !== OWN)) return false;
    state.editSetup = { from: page };
    showScreen(page, { silent: true });
    return true;
  }

  function returnToFlow() {
    state.editSetup = null;
    flushPersist(false);
    showScreen('guided-moment', { silent: true });
    var g = window.BirchGuidedRegistry;
    if (g && g.showRecap) g.showRecap();
  }

  function todayISO() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  // Push the journey's wording (and the date field's legal range) onto the
  // pre-rendered chrome. Called on boot and on every journey change.
  function applyJourneyCopy() {
    var f = flow();
    var setText = function (sel, text) {
      var el = $(sel);
      if (el && text) el.textContent = text;
    };
    setText('[data-rst-date-heading]', f.dateHeading);
    setText('[data-rst-date-sub]', f.dateSub);
    setText('[data-rst-date-label]', f.dateLabel);
    setText('[data-rst-age-label]', f.ageHeading);
    setText('[data-rst-multiples-label]', f.multiples);
    setText('[data-rst-first-heading]', f.firstHeading);
    var pair = $('.rst-tiles--pair');
    if (pair && f.firstHeading) pair.setAttribute('aria-label', f.firstHeading);
    // Page 2 carries a date, an age band, or (for adopting / baby's here) only
    // the age band. Both halves are toggled from the journey, so switching back
    // and forth can never leave the other one's field on screen.
    var dateField = $('[data-rst-datefield]');
    if (dateField) dateField.hidden = !!f.noDate;
    var ageField = $('[data-rst-agefield]');
    if (ageField) ageField.hidden = !f.age;
    var date = $('[data-rst-date]');
    if (date) {
      // A due date can't be in the past. Only the journeys that still ask for
      // a date reach this page at all, and every one of them looks forward —
      // the server agrees (validateDueDate rejects a past date for them).
      if (f.dateFuture) { date.min = todayISO(); date.removeAttribute('max'); }
      else { date.removeAttribute('min'); date.removeAttribute('max'); }
    }
  }

  // ── persistence ──────────────────────────────────────────────────────────
  var pendingPatch = null;
  var persistTimer = null;

  function deepMergePatch(base, patch) {
    Object.keys(patch).forEach(function (k) {
      if (k === 'answers' || k === 'progress') {
        base[k] = base[k] || {};
        Object.keys(patch[k]).forEach(function (ak) { base[k][ak] = patch[k][ak]; });
      } else {
        base[k] = patch[k];
      }
    });
    return base;
  }

  function persist(patch) {
    // Mirror into local state immediately — the server is eventually consistent.
    deepMergePatch(state.onboarding, JSON.parse(JSON.stringify(patch)));
    pendingPatch = deepMergePatch(pendingPatch || {}, patch);
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = setTimeout(function () { flushPersist(false); }, 600);
  }

  function flushPersist(unloading) {
    if (persistTimer) { clearTimeout(persistTimer); persistTimer = null; }
    if (!pendingPatch || !state.registry) return Promise.resolve();
    var body = JSON.stringify(pendingPatch);
    pendingPatch = null;
    // Direct fetch (not BirchRegistry.request) so it can use sendBeacon-style
    // keepalive on pagehide — so it must append the guest token itself. The
    // App Proxy strips cookies, so this is what carries guest identity.
    // NOTE: deliberately NOT added to the browse-feed call below — a per-guest
    // query param would fork that endpoint's edge-cache key and tank the hit
    // rate (the editor already learned this with a per-row `limit`).
    var path = BR.withGuestToken
      ? BR.withGuestToken('/registries/' + encodeURIComponent(state.registry.id) + '/onboarding')
      : '/registries/' + encodeURIComponent(state.registry.id) + '/onboarding';
    return fetch(API_BASE + path, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: !!unloading,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body,
    }).catch(function () { /* non-fatal — next write catches up */ });
  }

  window.addEventListener('pagehide', function () { flushPersist(true); });

  // ── screens ──────────────────────────────────────────────────────────────
  var SCREEN_NAMES = { 1: 'Where you are in your journey', 2: 'When they arrive', 3: 'Your first baby', own: 'What you already have', path: 'How would you like to build it', build: 'Build together', ai: 'Birch is building', done: 'All done' };

  function showScreen(name, opts) {
    state.screen = String(name);
    $all('[data-rst-screen]').forEach(function (el) {
      el.hidden = el.getAttribute('data-rst-screen') !== state.screen;
    });
    if (state.screen === OWN) { seedOwnAnswer(); reflectOwn(); }
    // The guided walkthrough runs as a FULL-SCREEN TAKEOVER — see that block
    // in registry-start.css for the whole arrangement. This is the only place
    // that state is ever set, on purpose: showScreen is the one function that
    // decides which [data-rst-screen] is visible, so "the guided screen is
    // showing" and "the takeover is up" cannot drift apart. Every other screen
    // takes it back off, including a browser Back out of the flow.
    setTakeover(state.screen === 'guided-moment');
    // CUR-156 — the fork is the FIRST page now, and no longer one of the
    // numbered setup pages (those are the Browse path that follows it). It
    // keeps the header but not the meter or "Skip all".
    var isSetupPage = isQuestionPage(state.screen);
    var isIntake = isSetupPage || state.screen === FORK;
    // §26 — the setup header (and its "Registry setup" eyebrow) belongs to the
    // questionnaire. The walkthrough has its own rail, count and mobile bar.
    var header = $('[data-rst-header]');
    if (header) header.hidden = !isIntake;
    $('[data-rst-skip-all]').hidden = !isSetupPage;
    $('[data-rst-meter]').hidden = !isSetupPage;
    $('[data-rst-meter-count]').hidden = !isSetupPage;
    if (isSetupPage) syncMeter();
    // CUR-151 — editing one answer from the guided recap is not a walk through
    // the questionnaire: no "Skip all", no page meter, and the eyebrow says
    // what is happening.
    if (state.editSetup) {
      $('[data-rst-skip-all]').hidden = true;
      $('[data-rst-meter]').hidden = true;
      $('[data-rst-meter-count]').hidden = true;
    }
    var eyebrow = $('.rst-eyebrow');
    if (eyebrow) eyebrow.textContent = state.editSetup ? 'Edit your setup' : 'Registry setup';
    syncBack();
    if (!opts || !opts.silent) {
      var live = $('[data-rst-live]');
      if (live) {
        var list = pages();
        var pos = list.indexOf(state.screen);
        live.textContent = (pos !== -1 ? 'Page ' + (pos + 1) + ' of ' + list.length + ', ' : '') + (SCREEN_NAMES[state.screen] || '');
      }
      try {
        var url = new URL(location.href);
        url.searchParams.set('step', state.screen);
        if (state.registry) url.searchParams.set('id', state.registry.id);
        history.replaceState(null, '', url.toString());
      } catch (_) {}
      var screenEl = $('[data-rst-screen="' + state.screen + '"]');
      var h = screenEl && screenEl.querySelector('.rst-heading, .rst-step__title');
      // preventScroll — see SCROLL POLICY at the top of this file.
      if (h) h.focus({ preventScroll: true });
    }
  }

  // ── the full-screen takeover ─────────────────────────────────────────────
  // ONE class on <html>, toggled from showScreen() alone. The CSS does the
  // rest (registry-start.css, § THE FULL-SCREEN TAKEOVER): site header,
  // announcement bar, breadcrumbs, mega-footer and the floating AI button go
  // away, the page stops scrolling, and the guided section becomes a fixed
  // full-viewport frame with its own scrolling middle. Nothing here edits a
  // theme template, and with JS off the class is never added — so every other
  // page, and every failure mode, renders exactly as before.
  //
  // THE JUMP TO 0. The window can be scrolled well down the page when the
  // takeover goes up (the shopper walked the intake questions to get here),
  // and once <html> is `overflow: hidden` nothing can scroll it back — so a
  // Back out of the flow would land them mid-page. Browsers do clamp a scroll
  // offset when the document shrinks, but not at a moment this code controls,
  // so it is done explicitly and instantly (jumpTo, which suppresses base.css's
  // site-wide `scroll-behavior: smooth`). Entering only: on the way out the
  // page is already at 0 and must stay where the shopper is put.
  var TAKEOVER_CLASS = 'grs-takeover';
  function setTakeover(on) {
    var html = document.documentElement;
    on = !!on;
    if (html.classList.contains(TAKEOVER_CLASS) === on) return;
    if (on) {
      jumpTo(0);
      html.classList.add(TAKEOVER_CLASS);
    } else {
      html.classList.remove(TAKEOVER_CLASS);
    }
  }

  // The dots follow THIS journey's page list: a two-question flow shows two.
  function syncMeter() {
    var list = pages();
    var pos = list.indexOf(state.screen);
    $all('.rst-meter__seg').forEach(function (seg, i) {
      seg.hidden = i >= list.length;
      seg.textContent = String(i + 1);
      seg.classList.toggle('rst-meter__seg--on', i <= pos);
      seg.classList.toggle('rst-meter__seg--now', i === pos);
    });
    $('[data-rst-meter-count]').textContent = (pos + 1) + ' of ' + list.length;
  }

  // base.css sets `html { scroll-behavior: smooth }` site-wide, and per spec a
  // scrollTo `behavior: 'auto'` defers to THAT — so the "instant" option was
  // animating. Suppressing the property for the duration of the jump is the one
  // form that lands hard in every browser, including the ones that reject the
  // newer `behavior: 'instant'` enum outright.
  function jumpTo(top) {
    var html = document.documentElement;
    var prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    try {
      window.scrollTo(0, top);
    } finally {
      html.style.scrollBehavior = prev;
    }
  }

  // Bring the setup header to the top of the viewport on entry (§6), as early
  // as the header exists in the DOM — the script tag sits below it, so the first
  // call lands at script-eval time, before the registry round-trip. Parking
  // after that fetch is what made the page open at the top and then visibly
  // jump a beat later. Every call is instant (never smooth) and re-parks only
  // if the header has actually drifted — from a late-loading theme header, or a
  // scroll the browser clamped against a document that was still parsing — so
  // the follow-up calls are silent no-ops in the normal case. `parkDone` closes
  // the window: after route(), or the moment the person scrolls themselves, the
  // flow never touches window scroll again.
  function parkFlow() {
    if (state.parkDone) return;
    // The guided walk HIDES the setup header (showScreen, §26) and parks its
    // own screen on every paint (guided-registry-screens.js's parkQuestion,
    // which has already run by the time route() resolves). Measuring the
    // hidden header here returned top 0 and turned this into "scroll UP 12px
    // from wherever you are" — and an upward scroll is exactly what the
    // theme's `sticky="scroll-up"` header answers by sliding in over the top
    // of the page, which put a resumed shopper's first question under it.
    // So on that screen this records where the page is, and moves nothing.
    if (state.screen === 'guided-moment') {
      state.parkY = window.pageYOffset;
      return;
    }
    var el = $('[data-rst-header]') || root;
    if (!el) return;
    try {
      // Leave a little air above so the header doesn't sit flush against a
      // sticky theme header.
      var top = Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - 12);
      if (Math.abs(window.pageYOffset - top) > 2) jumpTo(top);
    } catch (_) {
      el.scrollIntoView();
    }
    // Recorded AFTER the scroll (which is synchronous), so the scroll listener
    // below compares against where we actually landed, clamping included, and
    // doesn't read our own scroll as the person's.
    state.parkY = window.pageYOffset;
  }

  // Any scroll the person makes themselves ends the parking window — a later
  // correction that yanked the page back under them would be worse than the
  // drift it fixes.
  window.addEventListener('scroll', function () {
    if (state.parkDone || state.parkY === null) return;
    if (Math.abs(window.pageYOffset - state.parkY) > 4) state.parkDone = true;
  }, { passive: true });

  // First park: the header is already parsed above this script, so this is the
  // earliest a scroll can happen at all.
  parkFlow();

  // ── back navigation ──────────────────────────────────────────────────────
  // A flow-local stack, not browser history: every forward move pushes where it
  // came from, so Back always retraces the path the person actually walked
  // (including rail jumps) instead of replaying URL states.
  function snapshot() {
    return state.screen === 'build'
      ? { screen: 'build', stepIndex: state.stepIndex }
      : { screen: state.screen };
  }

  function pushHistory() {
    state.history.push(snapshot());
    if (state.history.length > 50) state.history.shift();
  }

  function canGoBack() {
    // A resumed session has no stack — walking back through earlier steps of the
    // plan is still meaningful, so the walkthrough falls back to step order.
    return state.history.length > 0 || (state.screen === 'build' && state.stepIndex > 0);
  }

  function syncBack() {
    var can = canGoBack() || !!state.editSetup;
    $all('[data-rst-back]').forEach(function (btn) { btn.hidden = !can; });
    // The header control would duplicate the walkthrough's own Back buttons.
    var header = $('[data-rst-back-header]');
    if (header) header.hidden = !can || state.screen === 'build';
  }

  function goBack() {
    var prev = state.history.pop();
    if (!prev) {
      if (state.screen === 'build' && state.stepIndex > 0) showStep(state.stepIndex - 1);
      return;
    }
    if (prev.screen === 'build') {
      showScreen('build', { silent: true });
      showStep(prev.stepIndex || 0);
    } else {
      showScreen(prev.screen);
    }
  }

  // Every forward hop out of a question page: flush what was answered, remember
  // where we came from, and land on whatever this journey asks next. A page
  // left blank simply carries no answer — that IS the skip (§8).
  function advanceFrom(page) {
    if (state.editSetup) {
      flushPersist(false);
      var edit = window.BirchGuidedSetupEdit;
      var nextEdit = edit ? edit.nextEditPage(page, state.onboarding.answers || {}, setupCfg()) : null;
      if (nextEdit) { showScreen(nextEdit, { silent: true }); return; }
      returnToFlow();
      return;
    }
    flushPersist(false);
    var next = pageAfter(page);
    // CUR-156 — these pages are the Browse path now (the fork comes first,
    // and Build together asks setup inside the guided flow), so the last one
    // ends in the editor, exactly where Browse always landed.
    if (!next) return finishBrowse();
    pushHistory();
    showScreen(next);
  }

  /** Browse: to the editor's browse view, the URL the fork's Browse card has
   *  always opened — once the answers just given are saved. */
  function finishBrowse() {
    var browseId = currentRegistryId();
    var url = '/pages/registry-edit'
      + (browseId ? '?id=' + encodeURIComponent(browseId) : '?create=1')
      + '#rcp-browse';
    var p = flushPersist(true) || Promise.resolve();
    Promise.resolve(p).then(function () { location.assign(url); });
    setTimeout(function () { location.assign(url); }, 400);
  }

  // ── chips ────────────────────────────────────────────────────────────────
  var MULTI_FIELDS = { lifestyle: 7, priorities: MAX_PRIORITIES };

  function chipsFor(field) { return $all('[data-rst-chip][data-field="' + field + '"]'); }

  function reflectChips(field) {
    var a = state.onboarding.answers || {};
    var val = a[field];
    chipsFor(field).forEach(function (chip) {
      var v = chip.getAttribute('data-value');
      var on = MULTI_FIELDS[field] != null
        ? Array.isArray(val) && val.indexOf(v) !== -1
        : String(val) === v && val != null;
      chip.classList.toggle('rst-chip--on', on);
      if (chip.hasAttribute('aria-pressed')) chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      else chip.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  function reflectChecks(field) {
    var a = state.onboarding.answers || {};
    $all('[data-rst-check][data-field="' + field + '"]').forEach(function (box) {
      box.checked = a[field] === true;
    });
  }

  /** The "How many?" select: revealed by the checkbox above it, and never
   *  showing a number the answers don't carry. */
  function reflectMultiples() {
    var on = (state.onboarding.answers || {}).multiples === true;
    var field = $('[data-rst-multiples-field]');
    if (field) field.hidden = !on;
    var sel = $('[data-rst-select][data-field="multiplesCount"]');
    // Parked on the default while the field is hidden — there is no "1" option,
    // so writing babyCount()'s 1 would silently leave the select where it was.
    if (sel) sel.value = String(on ? babyCount() : MULTIPLES_MIN);
  }

  /** A <select> answer. Numeric fields are stored as numbers, so the planner,
   *  the validator and the AI brief all see an integer rather than "3". */
  function setSelect(field, value) {
    if (field !== 'multiplesCount') return;
    var a = state.onboarding.answers = state.onboarding.answers || {};
    var n = parseInt(value, 10);
    if (MULTIPLES_COUNTS.indexOf(n) === -1) return;
    a.multiplesCount = n;
    persist({ answers: { multiplesCount: n } });
    reflectMultiples();
    // The count is part of the plan's quantities, so a cached plan built for a
    // different number is stale — drop the key, not the plan, so the fork keeps
    // painting while the re-plan is in flight.
    state.planAnswersKey = null;
    if (state.screen === OWN) reflectOwn();
  }

  var BOOL_FIELDS = { multiples: true, firstBaby: true };

  // sticky: a second tap on an already-chosen value keeps it chosen instead of
  // clearing it. Page 1's tiles are sticky because a re-tap there means
  // "yes, that one" — it re-triggers the advance (§7).
  function setChip(field, value, sticky) {
    var a = state.onboarding.answers = state.onboarding.answers || {};
    var patch = { answers: {} };
    if (MULTI_FIELDS[field] != null) {
      var list = Array.isArray(a[field]) ? a[field].slice() : [];
      var idx = list.indexOf(value);
      if (idx !== -1) {
        list.splice(idx, 1);
        if (field === 'priorities') state.priorityOrder = state.priorityOrder.filter(function (x) { return x !== value; });
      } else {
        if (field === 'priorities') {
          state.priorityOrder.push(value);
          // The 4th tap gently replaces the OLDEST selection — never an error.
          while (state.priorityOrder.length > MAX_PRIORITIES) {
            var oldest = state.priorityOrder.shift();
            list = list.filter(function (x) { return x !== oldest; });
          }
        }
        list.push(value);
        if (list.length > MULTI_FIELDS[field]) list = list.slice(-MULTI_FIELDS[field]);
      }
      patch.answers[field] = list.length ? list : null;
      a[field] = list.length ? list : undefined;
    } else {
      var parsed = BOOL_FIELDS[field] ? value === 'true' : value;
      if (a[field] === parsed && !sticky) {
        // second tap DESELECTS
        patch.answers[field] = null;
        delete a[field];
      } else {
        patch.answers[field] = parsed;
        a[field] = parsed;
      }
    }
    persist(patch);
    reflectChips(field);
    if (field === 'firstBaby' && a.firstBaby !== false) {
      // The `own` page has just left this flow. Its answer must leave with it:
      // a stored bigTicketWanted would otherwise reach the planner and the AI
      // builder as a claim about a family that never answered the question.
      if (a.bigTicketWanted != null) {
        delete a.bigTicketWanted;
        persist({ answers: { bigTicketWanted: null } });
      }
    }
    if (field === 'journey') {
      // An answer collected on a field the NEW journey never shows must not
      // survive the switch: it would still reach the planner and the AI brief,
      // framed as something the person never told us — a due date they never
      // gave, or an age band read as the child's actual age.
      var hasPage2 = pages().indexOf('2') !== -1;
      if ((!hasPage2 || flow().noDate) && a.arrivalDate != null) {
        delete a.arrivalDate;
        var input = $('[data-rst-date]');
        if (input) input.value = '';
        persist({ answers: { arrivalDate: null } });
        syncDateField();
      }
      // Only adopting and "baby's here" ask the age band, and only for them
      // does it mean anything — on any other journey a leftover value would
      // reach the planner as an age the child will not be.
      if (!flow().age && a.ageRange != null) {
        delete a.ageRange;
        persist({ answers: { ageRange: null } });
        reflectChips('ageRange');
      }
      applyJourneyCopy();
      if (isQuestionPage(state.screen)) syncMeter();
    }
  }

  // ── the `own` page — "which of these do you already have?" ───────────────
  // Storage is the COMPLEMENT of the tiles: `bigTicketWanted` is what they
  // still need, i.e. the tiles they un-marked. That keeps one meaning for the
  // field across the theme, the planner and the AI builder — "include these
  // after all" — and it makes an absent value mean exactly what it meant before
  // this page existed: nothing was asked.
  //
  // Empty is NOT absent. `[]` means "we asked, and they own all four", and it
  // is written the moment the page is shown, so a parent of twins who says they
  // have the double stroller is never overruled by the twins default that
  // produced their starting tiles.

  /** Keys pre-marked "have" when the page first opens. */
  function ownDefaults() {
    var a = state.onboarding.answers || {};
    // Two babies genuinely need a second sleep surface and a double or tandem
    // stroller, so those do not start marked. The shared-room items do.
    if (a.multiples === true) return TWINS_KEEP_DEFERRED.slice();
    return BIG_TICKET.map(function (b) { return b.key; });
  }

  /** Write the answer for the first time (or after multiples changed), so the
   *  page's own defaults are a real, stored answer rather than a guess the
   *  planner has to re-derive. */
  function seedOwnAnswer() {
    var a = state.onboarding.answers = state.onboarding.answers || {};
    if (Array.isArray(a.bigTicketWanted)) return;
    var owned = ownDefaults();
    var wanted = BIG_TICKET.map(function (b) { return b.key; })
      .filter(function (k) { return owned.indexOf(k) === -1; });
    a.bigTicketWanted = wanted;
    persist({ answers: { bigTicketWanted: wanted } });
  }

  function reflectOwn() {
    var a = state.onboarding.answers || {};
    var wanted = Array.isArray(a.bigTicketWanted) ? a.bigTicketWanted : [];
    $all('[data-rst-own]').forEach(function (tile) {
      var have = wanted.indexOf(tile.getAttribute('data-key')) === -1;
      tile.classList.toggle('rst-chip--on', have);
      tile.setAttribute('aria-pressed', have ? 'true' : 'false');
    });
    var note = $('[data-rst-own-twins]');
    if (note) {
      note.hidden = a.multiples !== true;
      if (a.multiples === true) {
        var n = babyCount();
        note.textContent = 'With ' + (MULTIPLES_NUMERAL[n] || 'more than one')
          + ' on the way we\u2019ve left the crib, bassinet and stroller in \u2014 another sleep surface and a '
          + (n > 2 ? 'multi-seat' : 'double') + ' stroller are usually a genuine second purchase. '
          + 'One monitor and one sound machine normally cover a shared room, so those still start marked.';
      }
    }
  }

  /** Tap = "actually, we still need this" (or the reverse). */
  function setOwn(tile) { setOwnKey(tile.getAttribute('data-key')); }

  /** CUR-156 — the same toggle by key, for the guided first-baby screen. */
  function setOwnKey(key) {
    if (!BIG_TICKET.some(function (b) { return b.key === key; })) return;
    var a = state.onboarding.answers = state.onboarding.answers || {};
    var wanted = Array.isArray(a.bigTicketWanted) ? a.bigTicketWanted.slice() : [];
    var i = wanted.indexOf(key);
    if (i === -1) wanted.push(key); else wanted.splice(i, 1);
    a.bigTicketWanted = wanted;
    // Always an array, never null: see the note above on empty vs absent.
    persist({ answers: { bigTicketWanted: wanted } });
    reflectOwn();
  }

  function setCheck(field, on) {
    var a = state.onboarding.answers = state.onboarding.answers || {};
    if (on) a[field] = true; else delete a[field];
    persist({ answers: (function () { var o = {}; o[field] = on ? true : null; return o; })() });
    reflectChecks(field);
    if (field === 'multiples') {
      // The count only exists while the box is ticked. Ticking seeds the
      // default (twins) as a REAL stored answer rather than a value the select
      // merely displays; un-ticking clears it, so a family who changed their
      // mind can never leave "triplets" behind on the row for the planner and
      // the AI brief to read as still true.
      if (on) { a.multiplesCount = babyCount(); persist({ answers: { multiplesCount: a.multiplesCount } }); }
      else if (a.multiplesCount != null) { delete a.multiplesCount; persist({ answers: { multiplesCount: null } }); }
      reflectMultiples();
      state.planAnswersKey = null;
    }
    if (field === 'multiples' && a.bigTicketWanted != null) {
      // Multiples decides which tiles the `own` page starts marked (two babies
      // need a second sleep surface and a double stroller). Changing it means
      // going BACK to page 2 — before that page — so re-seeding from the new
      // default is what someone correcting the answer expects, not a loss.
      delete a.bigTicketWanted;
      seedOwnAnswer();
      reflectOwn();
    }
  }

  // ── date field ───────────────────────────────────────────────────────────
  var DAY_MS = 86400000;

  // yyyy-mm-dd parsed as a LOCAL date. `new Date('2026-03-03')` is UTC
  // midnight, which reads back as the previous day west of Greenwich.
  function parseISODate(v) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ''));
    if (!m) return null;
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }

  function describeDate(v) {
    var d = parseISODate(v);
    if (!d) return '';
    var long = d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var days = Math.round((d.getTime() - today.getTime()) / DAY_MS);
    if (days === 0) return long + ' · today';
    var away = Math.abs(days);
    var span = away < 14 ? away + ' day' + (away === 1 ? '' : 's')
      : away < 90 ? Math.round(away / 7) + ' weeks'
      : Math.round(away / 30.4) + ' months';
    return long + ' · about ' + span + (days > 0 ? ' away' : ' ago');
  }

  function syncDateField() {
    var input = $('[data-rst-date]');
    if (!input) return;
    var v = input.value || '';
    var shell = $('[data-rst-date-shell]');
    if (shell) shell.classList.toggle('rst-datepick__shell--filled', !!v);
    var clear = $('[data-rst-date-clear]');
    if (clear) clear.hidden = !v;
    var read = $('[data-rst-date-read]');
    if (read) {
      var text = describeDate(v);
      read.textContent = text;
      read.hidden = !text;
    }
  }

  /** `opts.syncRegistry` (CUR-156): the guided timing screen writes the
   *  registry's own due date too, as an edit from the recap always has. */
  function commitDate(v, opts) {
    var a = state.onboarding.answers = state.onboarding.answers || {};
    if (v) a.arrivalDate = v; else delete a.arrivalDate;
    persist({ answers: { arrivalDate: v || null } });
    syncDateField();
    // CUR-151 — changed from the guided recap: the registry's own due date (what
    // Settings and the public page show) follows, via the same call Settings
    // makes. The onboarding write only fills an EMPTY dueDate, so without this
    // an edit here would leave the registry showing the old date.
    // A date input can fire `change` twice for one pick; send each value once.
    if ((state.editSetup || (opts && opts.syncRegistry)) && v && v !== state.dueDateSent && v >= todayISO() && state.registry && BR.API && BR.API.update) {
      state.dueDateSent = v;
      BR.API.update(state.registry.id, { dueDate: v }).catch(function () { state.dueDateSent = null; });
    }
  }

  // ── plan ─────────────────────────────────────────────────────────────────
  function planAnswers() {
    var a = state.onboarding.answers || {};
    var out = {};
    ['journey', 'arrivalDate', 'ageRange', 'multiples', 'multiplesCount', 'firstBaby', 'homeType', 'lifestyle', 'priorities', 'bigTicketWanted'].forEach(function (k) {
      if (a[k] != null) out[k] = a[k];
    });
    // The count is only meaningful alongside the flag. A row saved before the
    // box was un-ticked (or under a journey that never asks the question) must
    // not send "3" to a planner that has just been told there is one baby.
    if (out.multiples !== true) delete out.multiplesCount;
    return out;
  }

  function fetchPlan() {
    var answers = planAnswers();
    var key = JSON.stringify(answers);
    if (state.plan && state.planAnswersKey === key) return Promise.resolve(state.plan);
    return fetch(PLAN_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers: answers, categories: CATEGORIES }),
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var plan = data && data.ok && Array.isArray(data.steps) && data.steps.length
          ? { id: data.planId, generatedAt: new Date().toISOString(), source: data.source || 'rules', steps: data.steps }
          : { id: 'local', generatedAt: new Date().toISOString(), source: 'rules', steps: localPlan(answers) };
        state.plan = plan;
        state.planAnswersKey = key;
        renderFork();
        return plan;
      })
      .catch(function () {
        // The flow NEVER dead-ends on an AI-worker outage.
        state.plan = { id: 'local', generatedAt: new Date().toISOString(), source: 'rules', steps: localPlan(answers) };
        state.planAnswersKey = key;
        renderFork();
        return state.plan;
      });
  }

  // Mirrors guided/plan.ts's defaultWhy. Nothing paints `why` any more (the
  // step callout is gone), but it stays on the plan: it is a validated field on
  // the stored plan shape, and the offline fallback and the server must keep
  // producing the same one so a plan written by either reads the same on the
  // row. A category with nothing specific to say still says nothing.
  var WHY = {
    Nursery: 'Somewhere safe to sleep is the anchor of every registry.',
    Gear: 'Getting around — strollers, car seats and carriers.',
    Feeding: 'Bottles, feeding and mealtime, whatever feeding looks like for you.',
    Diaper: 'The everyday consumables you can never have too many of.',
    Grooming: 'Bath, skincare and the little health kit.',
    Clothes: 'The basics they live in (and outgrow fast).',
    Tech: 'Monitors and smart helpers for calmer nights.',
    Toys: 'Play and development from the first weeks on.',
    'Parent & Recovery': 'Comfort and recovery for you, not just the baby.',
  };

  /**
   * Subs removed on FIT grounds — the journey rules (adoption/surrogacy) and the
   * age band. Kept apart from the §2.3 deferral because the two mean opposite
   * things to the reader: one is "this doesn't suit your family", the other is
   * "you already own this". Telling someone they own a bassinet their child has
   * outgrown is the mistake this function exists to prevent. Mirrors the
   * corresponding rules in guided/plan.ts.
   */
  var AGE_ORDER = ['0_3mo', '3_6mo', '6_12mo', '12mo_plus'];
  function suppressedByFit(a) {
    var out = (a.journey === 'adopting' || a.journey === 'surrogacy')
      ? ['Breast Pumps', 'Nursing Pillows', 'Postpartum Recovery']
      : [];
    // Only adopting and "baby's here" are ever ASKED the age band, so only they
    // may act on it — a value on any other journey is stale, and reading it as
    // the child's age would drop the bassinet a newborn needs.
    var asksAge = a.journey === 'arrived' || a.journey === 'adopting';
    var stage = asksAge && a.ageRange && a.ageRange !== 'unsure' ? a.ageRange : null;
    if (stage && AGE_ORDER.indexOf(stage) >= AGE_ORDER.indexOf('6_12mo')) out = out.concat(['Bassinets']);
    return out;
  }

  /** `journey = planning` thins every non-core step to this many subs. */
  var PLANNING_MAX_SUBS = 2;

  /**
   * The BIG_TICKET entries the deferral removes for these answers. Note this is
   * computed from the ANSWERS, never inferred from a sub's absence — a sub can
   * be missing because it doesn't fit the child (suppressedByFit) or because the
   * `planning` thin cut it, and reporting either as "you already own this" is a
   * sentence the family never said.
   */
  function deferredFor(a) {
    if (a.firstBaby !== false) return [];
    var wantedKeys = Array.isArray(a.bigTicketWanted) ? a.bigTicketWanted : null;
    return BIG_TICKET.filter(function (b) {
      if (wantedKeys && wantedKeys.indexOf(b.key) !== -1) return false;   // they said they need it
      // Twins default, and ONLY with no answer at all — same gate as the
      // server. Once the `own` page has run, its answer is their word.
      if (a.multiples === true && !wantedKeys && TWINS_KEEP_DEFERRED.indexOf(b.key) === -1) return false;
      // They own one, and more than one baby is coming. Owning A crib is not
      // owning a crib EACH, so a per-baby durable is not covered by the answer
      // and the row stays — with its suggestion showing the SHORTFALL, not the
      // full count (see suggestedQty). Strollers are deliberately not per-baby:
      // two babies want one double, so an owned stroller still defers.
      if (babyCount() > 1 && PER_BABY_SUGGEST[b.sub] != null) return false;
      return true;
    });
  }

  /**
   * A category's subs after the rules that run BEFORE the deferral — the set the
   * deferral actually acts on. Same order as guided/plan.ts: fit rules, then the
   * `planning` thin, then (elsewhere) the deferral.
   */
  function eligibleSubs(cat, a) {
    var unfit = suppressedByFit(a);
    var out = cat.subs.filter(function (x) { return unfit.indexOf(x) === -1; });
    if (a.journey === 'planning' && cat.label !== 'Nursery' && cat.label !== 'Gear') {
      out = out.slice(0, PLANNING_MAX_SUBS);
    }
    return out;
  }

  /** "crib, bassinet and baby monitor" */
  function listPhrase(items) {
    if (items.length <= 1) return items[0] || '';
    if (items.length === 2) return items[0] + ' and ' + items[1];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  /** Sub label → the word the intake used for it ("Cribs" → "crib"). */
  var DEFERRED_LABEL = BIG_TICKET.reduce(function (m, b) { m[b.sub] = b.label; return m; }, {});

  // Mirror of deferralWhy in guided/plan.ts. The second clause is built from
  // what actually survived in this step, so it can never name a row the
  // merchandiser has since removed from the block.
  function deferralWhy(lostSubs, remaining) {
    // Always BIG_TICKET order — see the note on the server's copy.
    var ordered = BIG_TICKET.filter(function (b) { return lostSubs.indexOf(b.sub) !== -1; });
    var got = listPhrase(ordered.map(function (b) { return b.label; }));
    var head = "You've got the " + got;
    if (!remaining.length) return head + " — so there's nothing to do here.";
    var lead = listPhrase(remaining.slice(0, 2).map(function (x) { return x.toLowerCase(); }));
    return head + " — so we've started with " + lead + ' instead.';
  }

  // The macro order the server planner starts from (BASE_ORDER in
  // guided/plan.ts). NOT the theme's block order: the two used to disagree, so
  // an AI-worker outage silently reshuffled the walkthrough. Categories the
  // server has never heard of sort to the end, in block order.
  var BASE_ORDER = ['Nursery', 'Gear', 'Feeding', 'Diaper', 'Grooming', 'Clothes', 'Tech', 'Toys', 'Parent & Recovery'];
  function orderIndex(label) {
    var i = BASE_ORDER.indexOf(label);
    return i === -1 ? BASE_ORDER.length : i;
  }

  // Bundled fallback — the same core rules as the server planner, compressed.
  function localPlan(a) {
    var repeat = a.firstBaby === false;
    var smallSpace = a.homeType === 'apartment' || (a.priorities || []).indexOf('small_space') !== -1;
    var deferredSubs = deferredFor(a).map(function (b) { return b.sub; });
    // JOURNEY rules — mirrored from the server planner (guided/plan.ts) on
    // purpose. This fallback only runs when the AI worker is unreachable, which
    // is exactly when getting these wrong matters most: without the adopting /
    // surrogacy suppression an adopting family would be shown breast pumps and
    // postpartum recovery, which is the one omission the whole question exists
    // to guarantee.
    var unfit = suppressedByFit(a);
    // Lower sorts earlier. Mirrors the server's score bumps.
    var journeyRank = function (label) {
      if (a.journey === 'arrived') {
        // Baby's here — they have somewhere to sleep; feeding, clothes and play lead.
        if (label === 'Feeding') return -25;
        if (label === 'Clothes') return -35;
        if (label === 'Toys') return -40;
        if (label === 'Nursery') return 45;
      }
      if (a.journey === 'planning') {
        if (label === 'Nursery') return -60;
        if (label === 'Gear') return -55;
      }
      return 0;
    };

    // AGE BAND rules — also mirrored from guided/plan.ts. Only adopting and
    // "baby's here" ask this question, so only they may act on it: it is the
    // child's real age when they arrive. A value on any other journey is stale
    // (the journey switch clears it) and is ignored outright rather than being
    // read as an age nobody gave.
    var asksAge = a.journey === 'arrived' || a.journey === 'adopting';
    var stage = asksAge && a.ageRange && a.ageRange !== 'unsure' ? a.ageRange : null;
    var stageIdx = stage ? AGE_ORDER.indexOf(stage) : -1;
    var stageAtLeast = function (band) { return stageIdx >= 0 && stageIdx >= AGE_ORDER.indexOf(band); };
    // (A bassinet past ~6 months is the wrong sleep surface, not merely an
    // unlikely pick — suppressedByFit is where that lives. Never Car Seats.)
    // Past their moment by a year in, but some families still want them: pushed
    // to the back of their category, never taken away.
    var lateDemote = stageAtLeast('12mo_plus') ? ['Nursing Pillows', 'Postpartum Recovery'] : [];
    var agePromote = {};
    var ageDemote = {};
    if (stage === '0_3mo') {
      agePromote = { Nursery: ['Bassinets', 'Cribs', 'Crib Mattresses'], Feeding: ['Bottles', 'Nursing Pillows', 'Pacifiers'] };
      ageDemote = { Feeding: ['High Chairs'] };
    } else if (stage === '3_6mo') {
      agePromote = { Toys: ['Play Gyms & Mats'], Feeding: ['Teethers', 'Bottles'] };
    } else if (stageAtLeast('6_12mo')) {
      agePromote = { Feeding: ['High Chairs', 'Bibs'], Toys: ['Books', 'Play Gyms & Mats'], Nursery: ['Cribs', 'Crib Mattresses'] };
    }
    var ageRank = function (label) {
      if (stage === '3_6mo' && label === 'Toys') return -15;
      if (stageAtLeast('6_12mo')) {
        if (label === 'Feeding') return -20;
        if (label === 'Toys') return -30;
        if (label === 'Clothes') return -20;
        if (label === 'Nursery') return 30;
      }
      return 0;
    };
    var promote = function (subs, front) {
      if (!front || !front.length) return subs;
      var wanted = front.filter(function (s) { return subs.indexOf(s) !== -1; });
      return wanted.concat(subs.filter(function (s) { return wanted.indexOf(s) === -1; }));
    };
    var demote = function (subs, back) {
      if (!back || !back.length) return subs;
      var moved = back.filter(function (s) { return subs.indexOf(s) !== -1; });
      return subs.filter(function (s) { return moved.indexOf(s) === -1; }).concat(moved);
    };

    // Repeat parent: clothes and toys drop to the tail (hand-me-downs).
    // Mirrors the isRepeat block in guided/plan.ts.
    var repeatRank = function (label) {
      if (!repeat) return 0;
      return (label === 'Clothes' || label === 'Toys') ? 60 : 0;
    };

    // The `why` a rule wrote for a step, assembled in the SAME order the server
    // calls addWhy so a step that two rules speak to reads identically offline.
    // Not rendered — see WHY above — but stored, so the two planners must not
    // drift apart on it.
    var whyBits = {};
    var addWhy = function (label, text) {
      (whyBits[label] = whyBits[label] || []).push(text);
    };
    if (a.journey === 'adopting' || a.journey === 'surrogacy') {
      addWhy('Parent & Recovery', 'Comfort and rest for the weeks after they arrive.');
    }
    if (a.journey === 'arrived') addWhy('Feeding', "Baby's here — feeding, clothes and play come first.");
    if (a.journey === 'planning') {
      addWhy('Nursery', "You're planning ahead — we've started with the decisions worth researching longest.");
    }
    if (stage === '0_3mo') addWhy('Nursery', 'Building for the first three months, so the newborn sleep surface leads.');
    if (stage === '3_6mo') addWhy('Toys', 'Three to six months is when play and teething arrive, so those lead.');
    if (stageAtLeast('6_12mo')) {
      addWhy('Feeding', stage === '12mo_plus'
        ? 'A year in, mealtime and play are where a registry earns its keep.'
        : 'Six to twelve months is solids and sitting up, so mealtime leads.');
    }
    if (a.multiples === true) {
      var nWhy = babyCount();
      addWhy('Gear', "You're expecting " + (MULTIPLES_NUMERAL[nWhy] || 'more than one')
        + " — we've led with carriers and multi-seat picks, and multiplied the everyday quantities.");
    }
    if (repeat) addWhy('Diaper', "You've done this before — we've kept it to what wears out or gets outgrown.");
    if (smallSpace) addWhy('Nursery', 'Space is tight, so compact and convertible picks lead.');
    if ((a.lifestyle || []).indexOf('no_car') !== -1) {
      addWhy('Gear', "No car day-to-day, so carriers lead — a car seat still matters for rides, so it's here, just later.");
    }
    var ruleWhy = function (label) {
      var bits = whyBits[label];
      return bits && bits.length ? bits.join(' ') : '';
    };

    var qty2 = { Feeding: 1, Clothes: 1, Nursery: 1, Diaper: 1, Tech: 1 };
    var SLEEP_ANCHORS = ['Cribs', 'Bassinets'];
    var steps = CATEGORIES.map(function (c, i) {
      // Order matters: the journey and age rules remove on FIT grounds, the
      // deferral on ownership. A sub both would have taken belongs to the
      // first — attributing it to the deferral would tell someone they own a
      // bassinet when the truth is their child has outgrown one.
      var eligible = eligibleSubs(c, a);
      var lost = eligible.filter(function (s) { return deferredSubs.indexOf(s) !== -1; });
      var subs = eligible.filter(function (s) { return deferredSubs.indexOf(s) === -1; });
      // Age ordering runs BEFORE the home/lifestyle rules, same as the server,
      // so a small-space or no-car promotion still wins the front of the row.
      subs = promote(subs, agePromote[c.label]);
      subs = demote(subs, (ageDemote[c.label] || []).concat(lateDemote));
      // Two babies: carriers lead in Gear (a double stroller is not always the
      // way out of the house). Same position in the pipeline as the server.
      if (a.multiples === true && c.label === 'Gear') subs = promote(subs, ['Carriers']);
      if (smallSpace) {
        if (c.label === 'Nursery') subs = promote(subs, ['Bassinets', 'Play Yards & Travel Cribs', 'Cribs']);
        if (c.label === 'Gear') subs = promote(subs, ['Carriers', 'Strollers']);
      }
      var life = a.lifestyle || [];
      if (life.indexOf('no_car') !== -1 && c.label === 'Gear') {
        subs = promote(subs, ['Carriers']);
        // Demoted, never removed — a car seat is still needed for the ride home.
        subs = demote(subs, ['Car Seats']);
      }
      if (life.indexOf('travel_often') !== -1) {
        if (c.label === 'Nursery') subs = promote(subs, ['Play Yards & Travel Cribs']);
        if (c.label === 'Gear') subs = promote(subs, ['Diaper Bags']);
      }
      if ((life.indexOf('pets') !== -1 || life.indexOf('older_sibling') !== -1) && c.label === 'Grooming') {
        subs = promote(subs, ['Health']);
      }
      // Nursery leads the base order because the sleep surface is the anchor of a
      // registry. When it is the parent's OWN crib, that reason is gone with it —
      // see the same demote in the server planner.
      var demoteNursery = c.label === 'Nursery' && lost.length > 0
        && !subs.some(function (x) { return SLEEP_ANCHORS.indexOf(x) !== -1; });
      return {
        key: c.key, label: c.label, icon: c.icon,
        // A step the deferral touched says so, in place, instead of repeating a
        // line about the very thing we just took out.
        why: lost.length ? deferralWhy(lost, subs) : (ruleWhy(c.label) || WHY[c.label] || ''),
        subs: subs,
        // One unit per baby in the categories that are per-baby consumables.
        // MULTIPLES_MAX is the ceiling on both sides of the wire — the registry
        // worker validates plan.steps[].qty against the same range.
        qty: a.multiples === true && qty2[c.label] ? Math.min(babyCount(), MULTIPLES_MAX) : 1,
        // Base order (the theme's block order) plus the journey + age adjustments.
        _rank: orderIndex(c.label) * 10 + i * 0.1 + journeyRank(c.label) + ageRank(c.label)
          + repeatRank(c.label) + (demoteNursery ? 35 : 0),
        // A category emptied BY THE DEFERRAL stays in the walkthrough (see the
        // server planner) — it is the only route back to what we removed. One
        // emptied by the journey or age rules does not: nothing was removed on a
        // preference there, so there is nothing to offer back.
        _keep: subs.length > 0 || lost.length > 0,
      };
    })
      .filter(function (s) { var keep = s._keep; delete s._keep; return keep; })
      .sort(function (x, y) { return x._rank - y._rank; })
      .map(function (s) { delete s._rank; return s; });
    return steps;
  }

  function renderFork() {
    var sub = $('[data-rst-fork-sub]');
    // One line, unconditional: page 1 always carries an answer now (the default
    // journey), so the old "you told us nothing yet" variant could never fire.
    // The section pre-renders this same sentence, so the fork page never paints
    // an empty subhead while the plan is in flight — keep the two in step. The
    // per-card meta lines are gone: each choice is its title alone.
    if (sub) sub.textContent = 'You can edit everything after, either way.';
  }

  // ── guided walkthrough ───────────────────────────────────────────────────
  function iconSvg(name) {
    var t = document.querySelector('[data-rst-icons] [data-rst-icon="' + name + '"]')
      || document.querySelector('[data-rst-icons] [data-rst-icon="default"]');
    return t ? t.innerHTML : '';
  }

  // §13 — the step headers ship with an EMPTY icon slot (the section can't
  // inline the editor's icon set nine times), so fill them from the templates
  // once, at boot. Without this every step header renders a blank tile.
  function fillStepIcons() {
    $all('[data-rst-step-icon]').forEach(function (slot) {
      if (slot.firstElementChild) return;
      slot.innerHTML = iconSvg(slot.getAttribute('data-rst-step-icon') || 'default');
    });
  }

  // ── bundle screens (CUR-80 Part 1) ───────────────────────────────────────
  // Steps that offer an "Add all" bundle button, and its label — a bundle lets
  // someone add several related items (e.g. "the medicine cabinet") in one tap
  // instead of one at a time. Starting with `diaper` per the brief; add more
  // step keys here as bundles are designed for them. The button is additive —
  // the single-item "+ Add" on every card underneath still works exactly as
  // before.
  var BUNDLE_STEPS = {
    diaper: 'Add the diaper essentials',
  };

  // §13-style: the step panels ship with no bundle button in Liquid, so inject
  // one per bundle-enabled step, once, at boot — placed right after the step's
  // heading so it hides/shows with the rest of the panel via showStep()'s
  // existing [data-rst-step] toggle, with no separate show/hide logic needed.
  function renderBundleButtons() {
    $all('[data-rst-step]').forEach(function (panel) {
      var key = panel.getAttribute('data-rst-step');
      var label = BUNDLE_STEPS[key];
      if (!label) return;
      if ($('[data-rst-bundle-add]', panel)) return; // already inserted
      var head = $('.rst-step__head', panel);
      if (!head) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rst-add-btn rst-bundle-add';
      btn.setAttribute('data-rst-bundle-add', key);
      btn.textContent = label;
      head.insertAdjacentElement('afterend', btn);
    });
  }

  function addedInStep(key) {
    var counts = (state.onboarding.progress || {}).addedByStep || {};
    return counts[key] || 0;
  }

  function stepState(key) {
    var p = state.onboarding.progress || {};
    var visited = (p.done || []).indexOf(key) !== -1 || (p.skipped || []).indexOf(key) !== -1;
    // §11 — a tick means "you picked something here". Walking past a category
    // without adding anything is a skip, and skips don't tick.
    if (addedInStep(key) > 0) return 'done';
    return visited ? 'skipped' : 'pending';
  }

  function renderRail() {
    var rail = $('[data-rst-rail]');
    if (!rail || !state.plan) return;
    var current = state.plan.steps[state.stepIndex];
    // Moment headers group the rail's existing step list under the labels
    // from guided-registry-moments.js — presentation-only, one header per
    // new group, never touching s.subs or which step comes next. Dedupe by
    // "seen before", not "differs from the previous item": localPlan()'s
    // _rank sort (journeyRank/ageRank/repeatRank on top of BASE_ORDER) can
    // separate same-moment steps with a different-moment step in between
    // (e.g. journey === 'arrived' interleaves Diaper/Grooming/Nursery through
    // the Clothes/Tech/Toys run that share "The nursery") — a plain
    // previous-item check would then print that header more than once.
    var seenMoments = {};
    rail.innerHTML = state.plan.steps.map(function (s) {
      var moment = window.BirchGuidedMoments ? window.BirchGuidedMoments.groupLabelFor(s.key) : null;
      var header = '';
      if (moment && !seenMoments[moment]) {
        header = '<li class="rst-rail-moment" role="presentation">' + esc(moment) + '</li>';
        seenMoments[moment] = true;
      }
      var st = stepState(s.key);
      var n = addedInStep(s.key);
      var active = current && s.key === current.key;
      var stateStr = st === 'done' ? '✓ ' + n : st === 'skipped' ? '⤳' : '—';
      return header + '<li><button type="button" class="rst-rail-item' +
        (active ? ' rst-rail-item--active' : '') +
        (st === 'skipped' ? ' rst-rail-item--skipped' : '') +
        '" data-rst-goto="' + esc(s.key) + '"' + (active ? ' aria-current="step"' : '') + '>' +
        '<span class="rst-rail-item__icon">' + iconSvg(s.icon || 'default') + '</span>' +
        '<span class="rst-rail-item__label">' + esc(s.label) + '</span>' +
        '<span class="rst-rail-item__state">' + esc(String(stateStr)) + '</span>' +
        '</button></li>';
    }).join('');
    var meter = $('[data-rst-mobile-meter]');
    if (meter) meter.style.width = Math.round(((state.stepIndex + 1) / state.plan.steps.length) * 100) + '%';
    var mLabel = $('[data-rst-mobile-steps]');
    if (mLabel && current) mLabel.textContent = 'Step ' + (state.stepIndex + 1) + ' of ' + state.plan.steps.length + ' · ' + current.label;
    updateExitCard();
  }

  function updateExitCard() {
    var count = state.addedCount;
    var guest = BR.isGuest();
    var countEl = $('[data-rst-exit-count]');
    if (countEl) {
      // §20 — say what's true and nothing more: a count of selections. Where
      // they're stored is the sign-in line's job, below.
      countEl.textContent = count === 0
        ? 'Nothing added yet'
        : '✓ ' + count + ' item' + (count === 1 ? '' : 's') + ' selected';
    }
    var exitCta = $('[data-rst-exit-cta]');
    if (exitCta) exitCta.textContent = count === 0 ? 'Skip to my registry →' : 'Open my registry →';
    var signin = $('[data-rst-exit-signin]');
    if (signin) signin.hidden = !(guest && count > 0);
  }

  function showStep(index, opts) {
    if (!state.plan) return;
    if (index < 0) index = 0;
    if (index >= state.plan.steps.length) return finish();
    state.stepIndex = index;
    var step = state.plan.steps[index];
    $all('[data-rst-step]').forEach(function (el) {
      el.hidden = el.getAttribute('data-rst-step') !== step.key;
    });
    $('[data-rst-step-actions]').hidden = false;
    $('[data-rst-mobile-actions]').hidden = false;
    $('[data-rst-stepcount]').textContent = 'Step ' + (index + 1) + ' of ' + state.plan.steps.length;

    var panel = $('[data-rst-step="' + step.key + '"]');
    if (panel) {
      // Order + filter rows to the plan's subs. Hidden rows never fetch.
      var rows = $all('[data-rst-row]', panel);
      var byLabel = {};
      rows.forEach(function (r) { byLabel[r.getAttribute('data-feed-sub')] = r; });
      var visible = 0;
      rows.forEach(function (r) { r.hidden = r.hasAttribute('data-feed-empty') || step.subs.indexOf(r.getAttribute('data-feed-sub')) === -1; });
      step.subs.slice().reverse().forEach(function (sub) {
        var r = byLabel[sub];
        if (r) { r.parentNode.insertBefore(r, r.parentNode.querySelector('[data-rst-row]')); }
      });
      rows.forEach(function (r) { if (!r.hidden) { visible++; observeRow(r); } });
      // What the §2.3 deferral took OUT of this step, derived rather than
      // carried on the plan (the stored step shape is a validated allow-list —
      // see registryOnboarding.js — and this is recomputable from the answers).
      var lost = deferredHere(panel, step);
      // The undo lives where the missing thing would have been. One tap brings
      // the row back in its normal place AND records that they want it, so the
      // planner, the AI builder and a later refresh all agree. The tinted
      // callout that used to carry this sentence is gone; the link is not — it
      // is the only route back to a row the deferral took out.
      var restore = $('[data-rst-restore]', panel);
      if (restore) {
        restore.hidden = !(lost.length && visible > 0);
        var undo = $('[data-rst-restore-btn]', panel);
        if (undo && lost.length) {
          undo.textContent = 'Show ' + listPhrase(lost.map(function (b) { return b.label + 's'; })) + ' anyway';
        }
      }
      var empty = $('[data-rst-empty]', panel);
      if (empty) {
        empty.hidden = visible > 0;
        var emptyText = $('[data-rst-empty-text]', panel);
        if (emptyText) {
          emptyText.textContent = lost.length
            ? "You've got the " + listPhrase(lost.map(function (b) { return b.label; })) + ' — nothing to do here.'
            : "You told us you've got this covered — nothing to do here.";
        }
      }
    }
    var next = state.plan.steps[index + 1];
    $('[data-rst-next-step]').textContent = next ? 'Next: ' + next.label + ' →' : 'Finish →';
    $('[data-rst-next-step-mobile]').textContent = next ? 'Next →' : 'Finish →';

    persist({ path: 'guided', progress: { currentStep: step.key } });
    renderRail();
    syncBack();
    if (!opts || !opts.silent) {
      try {
        var url = new URL(location.href);
        url.searchParams.set('step', 'build');
        url.searchParams.set('cat', step.key);
        history.replaceState(null, '', url.toString());
      } catch (_) {}
      var live = $('[data-rst-live]');
      if (live) live.textContent = 'Step ' + (index + 1) + ' of ' + state.plan.steps.length + ', ' + step.label;
      var h = panel && panel.querySelector('.rst-step__title');
      // preventScroll — see SCROLL POLICY at the top of this file.
      if (h) h.focus({ preventScroll: true });
    }
  }

  // ── browse-feed rows ─────────────────────────────────────────────────────
  // Identity + dedupe helpers, mirrored from registry-unified.js (the editor)
  // so both surfaces reject the same duplicates: duplicate catalog listings
  // point at the SAME retailer page under different titles, and per-fetch
  // tracking params fork the raw URLs, so origin+path is the stable "same
  // product" key. normTitle is the server's `exclude` normalization.
  function normTitle(t) { return String(t || '').trim().toLowerCase().replace(/\s+/g, ' '); }
  function normFeedUrl(u) {
    if (!u) return '';
    try {
      var p = new URL(u);
      return (p.origin + p.pathname).toLowerCase().replace(/\/+$/, '');
    } catch (_) {
      return String(u).trim().toLowerCase();
    }
  }

  /** Identity keys for a candidate — any hit means "already on the registry". */
  function identityKeys(o) {
    var keys = [];
    if (o.externalUrl) keys.push('u:' + normFeedUrl(o.externalUrl));
    if (o.lushlifeProductId) keys.push('l:' + String(o.lushlifeProductId));
    if (o.shopifyProductId) keys.push('p:' + String(o.shopifyProductId));
    if (o.title) keys.push('t:' + normTitle(o.title));
    return keys;
  }
  function isAlreadyAdded(o) {
    return identityKeys(o).some(function (k) { return !!state.addedKeys[k]; });
  }
  function rememberAdded(o, on) {
    identityKeys(o).forEach(function (k) { if (on) state.addedKeys[k] = 1; else delete state.addedKeys[k]; });
  }

  /** What a row is already showing, by title AND by buy-link. */
  function rowPresent(scroll) {
    var titles = Object.create(null);
    var urls = Object.create(null);
    $all('.rst-pcard', scroll).forEach(function (c) {
      var t = normTitle(c.getAttribute('data-product-title') || c.getAttribute('data-detail-title'));
      if (t) titles[t] = 1;
      var u = normFeedUrl(c.getAttribute('data-external-url') || c.getAttribute('data-detail-url'));
      if (u) urls[u] = 1;
    });
    return { titles: titles, urls: urls };
  }

  var rowObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { rowObserver.unobserve(e.target); loadFeedRow(e.target); }
        });
      }, { rootMargin: '200px' })
    : null;

  function observeRow(row) {
    paintRowSuggestion(row);
    promoteMultiRider(row);
    if (row.dataset.feedLoaded) return;
    if (rowObserver) rowObserver.observe(row);
    else loadFeedRow(row);
  }

  /** The Add button. One tap is ONE item on every row, for every family — a
   *  button that silently added two was the one place in the flow where what
   *  happened and what the button said had to be reconciled after the fact.
   *  How many a family probably wants is advice now (paintRowSuggestion), and
   *  the stepper on the added card is how they act on it. */
  function addLabelHtml(title) {
    return '<button type="button" class="rst-add-btn" data-rst-add aria-label="Add '
      + title + ' to registry">+ Add</button>';
  }

  // ── two-rider strollers lead when there is more than one baby ────────────
  // Mirrors the stroller rule in the AI builder (negativeTitlePatternsFor /
  // multiRiderRank in build/search.ts) so the carousel and "Birch AI builds for
  // you" agree about what a twins family should see first. The catalog has no
  // "Double Strollers" subcategory to point a row at — doubles, tandems and
  // sit-and-stands sit in the same Strollers pool as the singles and lose to
  // them on ranking_score — so the ordering has to happen here, over whatever
  // the row is holding.
  var MULTI_RIDER_2 = /\b(double|duallie|dualie|tandem|twin|two[\s-]?seat|2[\s-]?seat|sit[\s'-]?n?[\s'-]?stand|sit[\s-]?and[\s-]?stand)\b/i;
  var MULTI_RIDER_3 = /\b(triple|triplet|quad|three[\s-]?seat|3[\s-]?seat|four[\s-]?seat|4[\s-]?seat|multi[\s-]?seat)\b/i;

  /** 0 sorts first. Triplets and up want the multi-seat frames ahead of the
   *  doubles; twins want the doubles ahead of everything. A single baby never
   *  gets here at all. */
  function multiRiderRank(title, babies) {
    var t = String(title || '');
    var three = MULTI_RIDER_3.test(t);
    var two = MULTI_RIDER_2.test(t);
    if (babies > 2) return three ? 0 : (two ? 1 : 2);
    return two ? 0 : (three ? 1 : 2);
  }

  /** Re-order a Strollers row so the frames that actually carry this family's
   *  babies come first. No-op for one baby, for every other row, and once the
   *  shopper has scrolled the carousel — moving cards under a hand that is
   *  already mid-scroll is worse than a card in the wrong place. */
  function promoteMultiRider(row, scroll) {
    if (!row) return;
    var babies = babyCount();
    if (babies < 2) return;
    var sub = row.getAttribute('data-feed-sub') || '';
    if (!/stroller/i.test(sub) || /accessor/i.test(sub)) return;
    scroll = scroll || $('[data-rst-row-scroll]', row);
    if (!scroll || scroll.scrollLeft > 0) return;
    var cards = $all('.rst-pcard', scroll);
    if (cards.length < 2) return;
    var ranked = cards.map(function (card, i) {
      return { card: card, rank: multiRiderRank(card.dataset.productTitle, babies), i: i };
    });
    // Nothing to float — leave the DOM completely alone.
    if (!ranked.some(function (r) { return r.rank < 2; })) return;
    ranked.sort(function (x, y) { return x.rank - y.rank || x.i - y.i; });
    ranked.forEach(function (r) { scroll.appendChild(r.card); });
  }

  /** The row's single suggestion chip — "Suggested: 8 for twins" — shown only
   *  when the answer is more than one. Re-run on each step entry so a changed
   *  intake answer (going Back and re-ticking multiples) is reflected without a
   *  re-render of the rows themselves. */
  function paintRowSuggestion(row) {
    if (!row) return;
    var el = $('[data-rst-row-suggest]', row);
    if (!el) return;
    var text = suggestText(row.getAttribute('data-feed-sub'));
    el.hidden = !text;
    el.textContent = text || '';
  }

  /** A row whose Birch collection is empty is drawn for the feed alone (see
   *  sections/registry-start.liquid). When the feed brings nothing either, the
   *  row would be a heading over a blank strip — hide it, and mark it so the
   *  step logic (showStep / show-anyway, which re-set `hidden` per step) keeps
   *  it hidden. */
  function hideRowIfEmpty(row, scroll) {
    if (scroll && $all('.rst-pcard', scroll).length) return;
    row.setAttribute('data-feed-empty', '');
    row.hidden = true;
  }

  function loadFeedRow(row) {
    if (row.dataset.feedLoaded) return;
    row.dataset.feedLoaded = '1';
    var cat = row.getAttribute('data-feed-cat');
    var sub = row.getAttribute('data-feed-sub');
    var scroll = row.querySelector('[data-rst-row-scroll]');
    if (!cat || !sub || !scroll) { hideRowIfEmpty(row, scroll); return; }
    var present = rowPresent(scroll);
    var excludeTitles = Object.keys(present.titles);
    // The `exclude` list is the set of Birch titles this row already renders —
    // byte-identical to what registry-edit sends for the same row, because both
    // surfaces render the same collection at the same cap. That parity is what
    // lets the two pages share one warm edge-cache entry per row.
    var excludeParam = excludeTitles.length ? '&exclude=' + encodeURIComponent(excludeTitles.join('\n')) : '';
    var PROXY = (window.BirchAI && window.BirchAI.routes && window.BirchAI.routes.aiProxy) || '/apps/ai';
    fetch(PROXY + '/browse-feed?cat=' + encodeURIComponent(cat) + '&sub=' + encodeURIComponent(sub) + '&limit=' + FEED_LIMIT + excludeParam, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) { hideRowIfEmpty(row, scroll); return; }
        appendFeedItems(row, scroll, (data.products || []), present);
        hideRowIfEmpty(row, scroll);
        row.dataset.feedOffset = String(data.nextOffset != null ? data.nextOffset : '');
        row.dataset.feedMore = data.hasMore ? '1' : '';
        wireCarouselScroll(row, cat, sub);
      })
      .catch(function () { hideRowIfEmpty(row, scroll); /* feed is best-effort — Birch rows still render */ });
  }

  // #12 — continuous horizontal scroll. Reaching the right edge of a carousel
  // pulls the next browse-feed page, the same way the editor and the See-all
  // page do. Nothing armed this before, which is why the rows dead-ended at
  // whatever page 1 returned.
  function wireCarouselScroll(row, cat, sub) {
    if (row.dataset.feedScrollWired === '1') return;
    var scroll = row.querySelector('[data-rst-row-scroll]');
    if (!scroll) return;
    row.dataset.feedScrollWired = '1';
    scroll.addEventListener('scroll', function () {
      if (row.dataset.feedMore !== '1' || row.dataset.feedLoadingMore === '1') return;
      if (scroll.scrollLeft + scroll.clientWidth >= scroll.scrollWidth - 320) loadMoreFeedRow(row, cat, sub);
    }, { passive: true });
  }

  function loadMoreFeedRow(row, cat, sub) {
    if (row.dataset.feedLoadingMore === '1' || row.dataset.feedMore !== '1') return;
    var scroll = row.querySelector('[data-rst-row-scroll]');
    if (!scroll) return;
    if ($all('.rst-pcard', scroll).length >= ROW_CARD_CAP) { row.dataset.feedMore = ''; return; }
    row.dataset.feedLoadingMore = '1';
    var offset = parseInt(row.dataset.feedOffset || '0', 10) || 0;
    var present = rowPresent(scroll);
    var PROXY = (window.BirchAI && window.BirchAI.routes && window.BirchAI.routes.aiProxy) || '/apps/ai';
    fetch(PROXY + '/browse-feed?cat=' + encodeURIComponent(cat) + '&sub=' + encodeURIComponent(sub) + '&limit=' + FEED_LIMIT + '&offset=' + offset, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) { row.dataset.feedMore = ''; return; }
        row.dataset.feedOffset = String(data.nextOffset != null ? data.nextOffset : offset);
        row.dataset.feedMore = data.hasMore ? '1' : '';
        appendFeedItems(row, scroll, (data.products || []), present);
      })
      .catch(function () { row.dataset.feedMore = ''; })
      .then(function () { row.dataset.feedLoadingMore = ''; });
  }

  /** Filter a feed batch against everything already shown / already added, and
   *  against itself, then append. Returns how many cards landed. */
  function appendFeedItems(row, scroll, items, present) {
    if (!items.length) return 0;
    var room = Math.max(0, ROW_CARD_CAP - $all('.rst-pcard', scroll).length);
    if (!room) return 0;
    var batchUrls = Object.create(null);
    var passed = items.filter(function (it) {
      var t = normTitle(it.title);
      if (t && present.titles[t]) return false;
      var u = normFeedUrl(it.externalUrl);
      if (u && (present.urls[u] || batchUrls[u])) return false;
      if (isAlreadyAdded({ externalUrl: it.externalUrl, lushlifeProductId: it.lushlifeProductId, title: it.title })) return false;
      if (t) present.titles[t] = 1;
      if (u) { present.urls[u] = 1; batchUrls[u] = 1; }
      return true;
    }).slice(0, room);
    if (!passed.length) return 0;
    scroll.insertAdjacentHTML('beforeend', passed.map(feedCardHtml).join(''));
    // A multiples family shopping for a stroller wants the two-rider ones
    // first, and a feed page that lands later must not bury them below the
    // singles it arrived after — so re-sort the whole row, not just the batch.
    promoteMultiRider(row, scroll);
    return passed.length;
  }

  /**
   * The trust badge for a feed-rendered card. Must stay element-for-element
   * identical to the Liquid-rendered one in sections/registry-start.liquid —
   * the two sit side by side in the same row — which in turn is the PDP
   * "What's inside" badge (blocks/bundle-contents.liquid) replicated: disc,
   * ring, then the "TrustScore" pill. role="img" carries the whole badge as one
   * label so the pill text isn't announced a second time.
   */
  function trustRingHtml(raw) {
    var n = Number(raw);
    if (!isFinite(n) || n <= 0) return '';
    var t = Math.max(1, Math.min(100, Math.round(n)));
    return '<div class="rst-pcard__trust product-card__trust-score-stack" style="--trust-score-value: ' + t + ';" role="img" aria-label="TrustScore ' + t + ' out of 100">' +
      '<div class="product-card__trust-score">' +
      '<svg class="product-card__trust-score-ring" viewBox="0 0 40 40" aria-hidden="true" focusable="false">' +
      '<circle class="product-card__trust-score-track" cx="20" cy="20" r="16" pathLength="100"></circle>' +
      '<circle class="product-card__trust-score-progress" cx="20" cy="20" r="16" pathLength="100"></circle>' +
      '</svg>' +
      '<span class="product-card__trust-score-value"><span class="product-card__trust-score-number">' + t + '</span></span>' +
      '</div>' +
      '<span class="product-card__trust-score-label">TrustScore</span>' +
      '</div>';
  }

  function feedCardHtml(it) {
    var price = typeof it.priceCents === 'number' ? it.priceCents : Math.round((it.price || 0) * 100);
    var title = String(it.title || '');
    var brand = it.brand || it.externalRetailer || '';
    // data-detail-* is the descriptor BirchDetailPopup reads — it's what makes
    // tapping the image or the title fetch the full description (§18).
    return '<div class="rst-pcard"' +
      ' data-external="1"' +
      ' data-external-url="' + esc(it.externalUrl || '') + '"' +
      ' data-external-retailer="' + esc(it.externalRetailer || '') + '"' +
      ' data-lushlife-id="' + esc(it.lushlifeProductId || '') + '"' +
      // The exact catalog variant this tile shows — its photo, price and buy
      // link all come from it, so the add records it rather than letting the
      // server re-resolve some other variant of the same product.
      ' data-external-variant-id="' + esc(it.externalVariantId || '') + '"' +
      ' data-external-color="' + esc(it.color || '') + '"' +
      ' data-external-size="' + esc(it.size || '') + '"' +
      ' data-product-title="' + esc(title) + '"' +
      ' data-product-image="' + esc(it.image || '') + '"' +
      ' data-price-cents="' + price + '"' +
      ' data-currency="USD"' +
      ' data-vendor="' + esc(brand) + '"' +
      ' data-product-type="' + esc(it.productType || '') + '"' +
      ' data-detail-source="external"' +
      ' data-detail-title="' + esc(title) + '"' +
      ' data-detail-image="' + esc(it.image || '') + '"' +
      ' data-detail-price-cents="' + price + '"' +
      ' data-detail-currency="USD"' +
      ' data-detail-brand="' + esc(brand) + '"' +
      ' data-detail-url="' + esc(it.externalUrl || '') + '"' +
      ' data-detail-llid="' + esc(it.lushlifeProductId || '') + '"' +
      (it.trustScore != null ? ' data-detail-trust="' + esc(String(it.trustScore)) + '"' : '') +
      '>' +
      '<div class="rst-pcard__img-wrap">' +
      '<button type="button" class="rst-pcard__img-btn" data-rst-detail aria-label="See details for ' + esc(title) + '">' +
      (it.image ? '<img src="' + esc(it.image) + '" alt="' + esc(title) + '" loading="lazy" class="rst-pcard__img" width="400" height="400">'
        : '<div class="rst-pcard__img-placeholder" aria-hidden="true">🌿</div>') +
      '</button>' +
      trustRingHtml(it.trustScore) +
      '</div>' +
      '<div class="rst-pcard__body">' +
      '<div class="rst-pcard__vendor">' + (esc(brand) || '&nbsp;') + '</div>' +
      '<button type="button" class="rst-pcard__title" data-rst-detail>' + esc(title.slice(0, 60)) + '</button>' +
      '<div class="rst-pcard__footer">' +
      '<span class="rst-pcard__price">' + esc(BR.formatMoney(price)) + '</span>' +
      '<span class="rst-pcard__action" data-rst-action>' +
      '<button type="button" class="rst-add-btn" data-rst-add aria-label="Add ' + esc(title) + ' to registry">+ Add</button>' +
      '</span>' +
      '</div></div></div>';
  }

  // ── add / quantity / remove ──────────────────────────────────────────────
  /** The most any one line on the registry can ask for. Was a bare `20` inside
   *  handleQty; named here because the guided flow's own stepper (CUR-80 Task
   *  13, window.BirchRegistryStart.setItemQuantity below) has to refuse exactly
   *  the same ceiling this one does — two surfaces disagreeing about the cap
   *  would mean a stepper that keeps counting up past a quantity the server
   *  side of the flow will not honour. */
  var MAX_ITEM_QTY = 20;

  function currentStep() { return state.plan ? state.plan.steps[state.stepIndex] : null; }

  function cardIdentity(card) {
    return card.dataset.external === '1'
      ? { externalUrl: card.dataset.externalUrl, lushlifeProductId: card.dataset.lushlifeId, title: card.dataset.productTitle }
      : { shopifyProductId: card.dataset.productId, title: card.dataset.productTitle };
  }

  /** Paint a card's action slot: the Add button, or the quantity stepper (§15).
   *  Quantity is a live control, not a one-way "✓ Added" dead end. */
  function renderAction(slot, opts) {
    if (!slot) return;
    var card = slot.closest('.rst-pcard');
    var title = esc((card && card.dataset.productTitle) || 'this item');
    if (!opts || !opts.itemId) {
      slot.innerHTML = addLabelHtml(title);
      delete slot.dataset.rstItemId;
      return;
    }
    var qty = Math.max(1, parseInt(opts.qty, 10) || 1);
    slot.dataset.rstItemId = String(opts.itemId);
    slot.innerHTML = '<span class="rst-qty" data-rst-qty>' +
      '<button type="button" class="rst-qty__btn" data-rst-qty-step="-1" aria-label="' +
        (qty === 1 ? 'Remove ' + title + ' from registry' : 'One fewer ' + title) + '">' + (qty === 1 ? '×' : '−') + '</button>' +
      '<span class="rst-qty__count" data-rst-qty-count aria-live="polite">' + qty + '</span>' +
      '<button type="button" class="rst-qty__btn" data-rst-qty-step="1" aria-label="One more ' + title + '">+</button>' +
      '</span>';
  }

  function setSlotBusy(slot, busy) {
    $all('button', slot).forEach(function (b) { b.disabled = !!busy; });
  }

  /** Build the addItem payload for one card. ONE. Always. The plan still
   *  carries a per-baby `qty` (it is a validated field on the stored plan —
   *  see registryOnboarding.js), but nothing adds by it any more: how many a
   *  family probably wants is said out loud on the row, and the stepper below
   *  is how they act on it. Shared by the single-card "+ Add" (handleAdd) and
   *  the bundle "Add all" button (bundleCandidates) so both send the server
   *  byte-identical payloads for the same card. */
  function cardPayload(card) {
    var qty = 1;
    if (card.dataset.external === '1') {
      return {
        source: 'external',
        externalUrl: card.dataset.externalUrl,
        externalRetailer: card.dataset.externalRetailer || null,
        lushlifeProductId: card.dataset.lushlifeId || null,
        externalVariantId: card.dataset.externalVariantId || undefined,
        externalColor: card.dataset.externalColor || undefined,
        externalSize: card.dataset.externalSize || undefined,
        trustScore: card.dataset.detailTrust ? Number(card.dataset.detailTrust) : undefined,
        title: card.dataset.productTitle,
        imageUrl: card.dataset.productImage || null,
        priceCents: parseInt(card.dataset.priceCents, 10) || null,
        currency: card.dataset.currency || 'USD',
        productType: card.dataset.productType || null,
        priority: 'nice_to_have',
        quantityWanted: qty,
      };
    }
    // WHAT YOU SEE IS WHAT YOU ADD — the tile renders one variant's photo and
    // price (snippets/registry-card-data.liquid), so the add registers that
    // same variant instead of letting the server pick an invisible default.
    // The option is changeable afterwards in the product sheet's picker.
    return {
      source: 'shopify',
      shopifyProductId: card.dataset.productId,
      shopifyVariantId: card.dataset.variantId || null,
      shopifyHandle: card.dataset.productHandle,
      title: card.dataset.productTitle,
      imageUrl: card.dataset.productImage || null,
      priceCents: parseInt(card.dataset.priceCents, 10) || null,
      currency: card.dataset.currency || 'USD',
      priority: 'nice_to_have',
      quantityWanted: qty,
    };
  }

  /** The non-DOM half of recording a successful add: mirrors the item locally
   *  (upsertLocalItem — the product sheet's variant picker matches Add⇄Remove
   *  on the item's variant id, so it has to see this add without a refetch),
   *  the added-count / rail-tick counters, AND the feed-dedupe fingerprint
   *  (rememberAdded — read by isAlreadyAdded() to filter freshly-fetched
   *  /browse-feed rows). That fingerprint used to be keyed off the DOM card
   *  via cardIdentity(card), but the identity it derives — externalUrl /
   *  lushlifeProductId / shopifyProductId / title — is just a read of the
   *  card's dataset, and the server-returned `item` already carries the same
   *  fields (see paintExistingItems above, which keys off `it.*` the exact
   *  same way for items loaded with no card in sight). Deriving it from
   *  `item` instead of a card is what lets this be the ONE place that keys
   *  the fingerprint, so CUR-80 Task 9's view-more panel — whose cards are
   *  `.grs-slot`, a JS-built port of `.rst-pcard` rather than the card itself,
   *  with no `[data-rst-action]` slot or DOM-card identity for cardIdentity() —
   *  gets the fingerprint too, split out of recordAddedItem (below) so it can
   *  share the EXACT SAME bookkeeping without being forced through
   *  card/slot-shaped DOM it doesn't have. Returns the quantity now on the
   *  server, same as recordAddedItem always resolved to before this split. */
  function applyAddedItem(item) {
    upsertLocalItem(item);
    rememberAdded({ externalUrl: item.externalUrl, lushlifeProductId: item.lushlifeProductId, shopifyProductId: item.shopifyProductId, title: item.title }, true);
    var stepKey = (currentStep() || {}).key || 'other';
    var known = state.added[item.id];
    var newQty = item.quantityWanted || 1;
    if (!known) {
      state.added[item.id] = { stepKey: stepKey, qty: newQty };
      state.addedCount++;
      bumpStepCount(stepKey, 1);
    } else {
      known.qty = newQty;
    }
    return newQty;
  }

  /** applyAddedItem's EXACT counterpart for a removal the server has already
   *  accepted. Every line above has an undo here, in the same order:
   *    - the local mirror row goes (dropLocalItem, so the product sheet's
   *      Add⇄Remove state stops claiming this item is on the registry);
   *    - the feed-dedupe fingerprint is cleared (rememberAdded(…, false)) —
   *      otherwise isAlreadyAdded() keeps filtering this product out of every
   *      freshly-fetched row and the shopper can never add it back;
   *    - state.added / state.addedCount / the persisted per-step count all
   *      come back down by exactly the one row the add put up.
   *  The identity is derived from the MIRRORED ITEM (same four fields
   *  applyAddedItem keys off `item`), so this works for an item with no card
   *  on screen — which is the guided flow's whole situation. A caller that
   *  does have a card can pass its cardIdentity() as `identity` to use that
   *  instead, which is what handleQty does.
   *
   *  Unlike the add path (where repainting the slot is the caller's business —
   *  see recordAddedItem) this DOES unbind every action slot bound to the id:
   *  a stepper left pointing at a deleted item is a control that can only
   *  fail, and the caller often has no way to find those slots (the guided
   *  flow, again). Callers batch renderRail() themselves, same as the adds. */
  function applyRemovedItem(itemId, identity) {
    var mirrored = localItemById(itemId);
    var ident = identity || (mirrored ? {
      externalUrl: mirrored.externalUrl,
      lushlifeProductId: mirrored.lushlifeProductId,
      shopifyProductId: mirrored.shopifyProductId,
      title: mirrored.title,
    } : null);
    if (ident) rememberAdded(ident, false);
    dropLocalItem(itemId);
    var meta = state.added[itemId];
    if (meta) {
      delete state.added[itemId];
      state.addedCount = Math.max(0, state.addedCount - 1);
      bumpStepCount(meta.stepKey, -1);
    }
    slotsForItem(itemId).forEach(function (slot) { renderAction(slot, null); });
  }

  /** applyAddedItem's counterpart for a quantity the server has already
   *  accepted. Deliberately does NOT touch state.addedCount or the per-step
   *  count: both count ROWS ("things you decided to add"), not units — the
   *  exit card says "3 items selected" about three decisions, and a row going
   *  from 2 to 3 is not a fourth decision. Same rule handleQty has always
   *  followed (it only moves those counters when a step down to 0 deletes the
   *  row). Repaints every bound action slot for the same reason the removal
   *  does. Returns the quantity now in effect. */
  function applyItemQuantity(itemId, qty) {
    var meta = state.added[itemId] || { stepKey: (currentStep() || {}).key || 'other', qty: qty };
    meta.qty = qty;
    state.added[itemId] = meta;
    patchLocalItem(itemId, { quantityWanted: qty });
    slotsForItem(itemId).forEach(function (slot) { renderAction(slot, { itemId: itemId, qty: qty }); });
    return qty;
  }

  /** Bookkeeping for one CARD whose add (or qty-bump merge) just succeeded on
   *  the server: applyAddedItem's shared bookkeeping (which already covers the
   *  feed-dedupe fingerprint off `item`'s own fields — see its comment), plus
   *  the one piece that only makes sense for a real `.rst-pcard` — repainting
   *  the card's own action slot. Shared by handleAdd (one card) and
   *  handleBundleAdd (several cards at once) so both refresh EXACTLY the same
   *  UI state the same way — callers batch the renderRail() call themselves
   *  once, after all of their cards are recorded. */
  function recordAddedItem(card, slot, item) {
    if (!item) return;
    var newQty = applyAddedItem(item);
    renderAction(slot, { itemId: item.id, qty: newQty });
  }

  function handleAdd(btn) {
    var card = btn.closest('.rst-pcard');
    var slot = btn.closest('[data-rst-action]');
    if (!card || !slot || !state.registry || btn.disabled) return;
    setSlotBusy(slot, true);
    BR.API.addItem(state.registry.id, cardPayload(card))
      .then(function (res) {
        var item = res && res.item;
        if (!item) throw new Error('no_item');
        recordAddedItem(card, slot, item);
        renderRail();
        BR.showToast(res && res.merged ? 'Quantity increased' : 'Added to registry');
      })
      .catch(function () {
        renderAction(slot, null);
        BR.showToast("Couldn't add that — please try again.");
      });
  }

  /** CUR-80 Task 9 — the view-more panel's add path (guided-registry-recs.js).
   *  Its cards aren't `.rst-pcard`s (no card/slot to hand handleAdd), so it
   *  can't call handleAdd(btn) directly — but this runs the IDENTICAL network
   *  call handleAdd makes (BR.API.addItem(state.registry.id, payload), same
   *  endpoint, same body shape via the same cardPayload()-shaped `payload` the
   *  caller builds) and the identical non-DOM success-path bookkeeping
   *  (applyAddedItem + renderRail(), which also refreshes the exit card's
   *  count — see updateExitCard() inside renderRail()) — so an add from the
   *  view-more panel updates the same rail/exit-card state a normal add does.
   *  `onDone({ ok, item })` always fires, once, synchronously-or-later; it
   *  never throws. Exposed on window.BirchRegistryStart — this file's IIFE
   *  otherwise has no external surface, and state.registry / renderRail /
   *  applyAddedItem are private to it. */
  function addFromViewMore(payload, onDone) {
    if (!state.registry) { if (onDone) onDone({ ok: false }); return; }
    BR.API.addItem(state.registry.id, payload)
      .then(function (res) {
        var item = res && res.item;
        if (!item) throw new Error('no_item');
        applyAddedItem(item);
        renderRail();
        BR.showToast(res && res.merged ? 'Quantity increased' : 'Added to registry');
        if (onDone) onDone({ ok: true, item: item });
      })
      .catch(function () {
        BR.showToast("Couldn't add that — please try again.");
        if (onDone) onDone({ ok: false });
      });
  }

  /** CUR-80 Task 13 — remove ONE item the guided flow already added, by the
   *  id addFromViewMore handed back. The network half is BR.API.removeItem
   *  verbatim (the DELETE the App Proxy only forwards as POST +
   *  X-HTTP-Method-Override; it rides BirchRegistry.request, so it gets the
   *  guest token and the ensureGuestSession() await for free — the guided demo
   *  runs as a guest and a write without that token 401s). The local half is
   *  applyRemovedItem, the same bookkeeping handleQty's step-to-zero and the
   *  product sheet's own Remove run, so a removal here leaves the rail, the
   *  exit-card count, the feed dedupe and the item mirror exactly as they were
   *  before the add. `onDone({ ok, itemId })` always fires, once; never
   *  throws. Exposed for the same reason addFromViewMore is: state.registry
   *  and every one of those bookkeeping functions are private to this IIFE. */
  function removeItemById(itemId, onDone) {
    if (!itemId || !state.registry) { if (onDone) onDone({ ok: false, itemId: itemId || null }); return; }
    BR.API.removeItem(state.registry.id, itemId)
      .then(function () {
        applyRemovedItem(itemId);
        renderRail();
        BR.showToast('Removed');
        if (onDone) onDone({ ok: true, itemId: itemId });
      })
      .catch(function (err) {
        // A 409 is the server refusing on purpose (the item has been gifted or
        // has pledged contributions — contributionGuards' guardDelete), and its
        // message says why. "Try again" would be wrong: it will never succeed.
        var refusal = err && err.status === 409 && err.body && err.body.message;
        BR.showToast(refusal || "Couldn't remove that — please try again.");
        if (onDone) onDone({ ok: false, itemId: itemId });
      });
  }

  /** CUR-80 Task 13 — set an already-added item's quantity outright (not a
   *  delta: the caller's stepper knows what it wants the number to BE, and a
   *  delta applied to a stale local count would drift). BR.API.updateItem is
   *  the same tunnelled PATCH the card stepper uses, guest token and all;
   *  applyItemQuantity is the same local half.
   *
   *  Out of range (< 1, > MAX_ITEM_QTY, not a number) is REFUSED WITHOUT A
   *  NETWORK CALL and reported as `ok: false` with the quantity still in
   *  effect — going to 0 is a removal, which is a different decision with a
   *  different undo, so this never silently deletes a row. `onDone({ ok,
   *  itemId, quantity })` always fires, once; `quantity` is what the client
   *  should now show either way (the new value on success, the unchanged one
   *  on failure). Never throws. */
  function setItemQuantity(itemId, quantity, onDone) {
    var known = state.added[itemId];
    var currentQty = (known && known.qty) || null;
    var qty = parseInt(quantity, 10);
    if (!itemId || !state.registry || !(qty >= 1) || qty > MAX_ITEM_QTY) {
      if (onDone) onDone({ ok: false, itemId: itemId || null, quantity: currentQty });
      return;
    }
    BR.API.updateItem(state.registry.id, itemId, { quantityWanted: qty })
      .then(function () {
        applyItemQuantity(itemId, qty);
        if (onDone) onDone({ ok: true, itemId: itemId, quantity: qty });
      })
      .catch(function () {
        BR.showToast("Couldn't update the quantity — please try again.");
        if (onDone) onDone({ ok: false, itemId: itemId, quantity: currentQty });
      });
  }

  /** CUR-80 Task 16 — re-read THIS registry from the server and hand the caller
   *  its real, stored items. The guided summary used to render a session-local
   *  tally it had invented client-side (it survived no reload, double-counted a
   *  product added under two moments, and counted an add that only bumped a
   *  quantity); this is the read that replaces it.
   *
   *  Deliberately the SAME call the rest of the theme already makes —
   *  BR.API.get → BirchRegistry.request, which awaits ensureGuestSession() and
   *  attaches the guest token — not a second items endpoint invented here. The
   *  guided flow runs as a guest and a read without that token 401s.
   *
   *  It also re-seeds this file's own mirrors from the answer (the items list,
   *  state.added, state.addedCount, the feed-dedupe fingerprints) so the rail
   *  count and the summary can never disagree about the same registry.
   *  `state.registry` itself is NOT swapped wholesale: that row carries
   *  `onboarding`, and replacing the live answers with a server copy mid-flow is
   *  exactly the race documented above resolveRegistry(). The PERSISTED
   *  per-step counts are left alone too — bumpStepCount writes onboarding, and
   *  a read must never write.
   *
   *  `onDone({ ok, registry, items })` always fires, once; never throws. On a
   *  failure `ok` is false and `items` is empty, so the caller has to SAY it
   *  could not read the registry rather than quietly presenting a local guess
   *  as the truth. */
  function refreshRegistry(onDone) {
    var done = function (r) { if (onDone) onDone(r); };
    if (!state.registry || !state.registry.id) { done({ ok: false, registry: null, items: [] }); return; }
    BR.API.get(state.registry.id).then(function (d) {
      var reg = d && d.registry;
      if (!reg || !Array.isArray(reg.items)) throw new Error('no_registry');
      var items = reg.items.filter(function (it) { return it && it.id && it.status !== 'removed'; });
      state.registry.items = reg.items;
      var live = Object.create(null);
      items.forEach(function (it) {
        live[it.id] = 1;
        var known = state.added[it.id];
        state.added[it.id] = { stepKey: known ? known.stepKey : 'other', qty: it.quantityWanted || 1 };
        rememberAdded({ externalUrl: it.externalUrl, lushlifeProductId: it.lushlifeProductId, shopifyProductId: it.shopifyProductId, title: it.title }, true);
      });
      Object.keys(state.added).forEach(function (id) { if (!live[id]) delete state.added[id]; });
      state.addedCount = items.length;
      renderRail();
      done({ ok: true, registry: reg, items: items });
    }).catch(function () { done({ ok: false, registry: null, items: [] }); });
  }

  window.BirchRegistryStart = window.BirchRegistryStart || {};
  window.BirchRegistryStart.addFromViewMore = addFromViewMore;
  window.BirchRegistryStart.removeItem = removeItemById;
  window.BirchRegistryStart.setItemQuantity = setItemQuantity;
  window.BirchRegistryStart.refreshRegistry = refreshRegistry;
  // CUR-179 — exported for unit/new-registry-create.test.cjs.
  window.BirchRegistryStart.resolveRegistry = function () { return resolveRegistry(); };
  window.BirchRegistryStart.NEW_REGISTRY_FAILED = NEW_REGISTRY_FAILED;
  // The registry the flow is writing into, for a caller that needs to scope
  // per-registry bookkeeping to it (the guided summary's moment attribution).
  // Null until resolveRegistry() has settled.
  window.BirchRegistryStart.registryId = function () { return state.registry ? state.registry.id : null; };
  // The ceiling setItemQuantity enforces, published so a caller's own stepper
  // can stop AT it instead of discovering it as a rejection.
  window.BirchRegistryStart.maxItemQuantity = MAX_ITEM_QTY;

  // ── bundle "Add all" (CUR-80 Part 1) ─────────────────────────────────────
  // Best-effort signed-in customer email for the REGISTRY_CREATED milestone —
  // mirrors registry-store.js's customerEmailHint(), which BR.API.addItem
  // attaches automatically. addBundle() below goes around BR.API on purpose
  // (see its own comment), so it has to attach this itself.
  function customerEmailHint() {
    try {
      var form = document.getElementById('registry-create-form');
      return (form && form.dataset.customerEmail) || window.__birchCustomerEmail || '';
    } catch (_e) { return ''; }
  }

  /** POST /registries/:id/items/bulk — the bundle-add companion to
   *  BR.API.addItem. Direct fetch (not BirchRegistry.request), same reason as
   *  flushPersist above: it has to append the guest token itself, since the
   *  App Proxy strips cookies and that token is the only thing that carries
   *  guest identity. `onDone` always fires — with the parsed response, or
   *  null on a network failure — never throws. */
  function addBundle(items, onDone) {
    var emailedItems = items.map(function (it) {
      return Object.assign({ customerEmail: customerEmailHint() }, it);
    });
    var path = '/registries/' + encodeURIComponent(state.registry.id) + '/items/bulk';
    fetch(API_BASE + (BR.withGuestToken ? BR.withGuestToken(path) : path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: emailedItems }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { if (onDone) onDone(data); })
      .catch(function () { if (onDone) onDone(null); });
  }

  /** CUR-80 guided-flow Task 5 — the bundle SCREENS' (guided-registry-
   *  screens.js's renderBundle) "Add all". Reuses addBundle() verbatim — the
   *  SAME bulk route, and critically the SAME BR.withGuestToken() attachment
   *  it already does inside addBundle (see that function's own comment): the
   *  guided-flow demo this is built for runs as a guest (not signed in), and
   *  an earlier implementer found that every bundle add from a guest 401s
   *  without that token. A reimplemented fetch here that forgot it would
   *  make "Add all" silently fail in front of an audience.
   *
   *  Then runs the SAME non-DOM bookkeeping handleBundleAdd's onDone does for
   *  each item the server actually returned (applyAddedItem — mirrors the
   *  item locally, bumps counters, keys the feed-dedupe fingerprint), and
   *  refreshes the rail ONCE for the whole batch — there's just no
   *  `.rst-pcard`/action-slot to repaint here, since the guided shell's rows
   *  are plain markup, not browse-row cards.
   *
   *  `onDone({ ok, addedCount, failedCount, results })` always fires, once,
   *  synchronously-or-later; never throws.
   *
   *  `results` (CUR-80 Task 13) is ONE ENTRY PER INPUT ITEM, aligned by index
   *  with the `items` array the caller passed in — the bulk route builds its
   *  own results[] that way (see proxy.api.registries.$id.items.bulk.jsx's
   *  "align results[i] with the request's items[i]" comment) and this passes
   *  that alignment straight through instead of discarding it. Each entry is
   *  `{ ok: true, itemId, quantity, merged, skipped, status }` or
   *  `{ ok: false }`.
   *
   *  As of Task 14 the guided bundle screens send ONE PAYLOAD PER ROW carrying
   *  that row's real `quantityWanted`, not one payload per unit — so
   *  `addedCount` (and therefore the toast below) counts ROWS, which is what
   *  the registry actually gains. The qty-6 row that used to post six identical
   *  payloads and toast "Added 6 items" while the registry gained a single row
   *  at qty 6 now posts one payload and toasts "Added 1 item". `ok` is
   *  true iff at least one item landed — a partial failure still advances the
   *  guided flow (matches
   *  handleBundleAdd's own "Added N — M couldn't be added" partial-success
   *  toast, just surfaced to the caller instead of assumed). Exposed on
   *  window.BirchRegistryStart alongside addFromViewMore (Task 9) — same
   *  reason: this file's IIFE has no other external surface, and
   *  state.registry / renderRail / applyAddedItem are private to it. */
  function addBundleItems(items, onDone) {
    if (!state.registry) { if (onDone) onDone({ ok: false, addedCount: 0, failedCount: items.length, results: [] }); return; }
    addBundle(items, function (data) {
      var results = data && data.ok && Array.isArray(data.results) ? data.results : null;
      if (!results) {
        BR.showToast("Couldn't add the bundle — please try again.");
        if (onDone) onDone({ ok: false, addedCount: 0, failedCount: items.length, results: [] });
        return;
      }
      var addedCount = 0;
      var failedCount = 0;
      // One entry per INPUT item, positionally — `items[i]` produced
      // `outcomes[i]`. Built from the server's own per-input results array,
      // never compacted (a `filter(Boolean)` here is exactly what would
      // destroy the alignment the caller needs to map an id back to its row).
      var outcomes = items.map(function () { return { ok: false }; });
      results.forEach(function (res, i) {
        if (!res) { failedCount++; return; }
        // CUR-80 Task 14 — the bulk route now labels every row with its own
        // `index` (present even on error rows) plus `status` / `itemId` /
        // `quantityWanted`. Those are preferred where present and every one of
        // them falls back to the legacy shape (`item`, `merged`, `skipped`,
        // `error`), which is still emitted unchanged — so this reads an older
        // worker exactly the way it always did. `res.index` is only trusted
        // when it addresses a real slot; otherwise the array position stands,
        // which is the pre-Task-14 contract.
        var at = typeof res.index === 'number' && res.index >= 0 && res.index < outcomes.length ? res.index : i;
        var item = res.item || null;
        var itemId = res.itemId || (item && item.id) || null;
        if (res.status === 'error' || !itemId) { failedCount++; return; }
        // Only a full row can be mirrored locally; an id-without-row (a shape
        // no current worker emits) still counts as added, because the server
        // says it is — it just can't seed the rail/dedupe bookkeeping.
        if (item) applyAddedItem(item);
        addedCount++;
        outcomes[at] = {
          ok: true,
          itemId: itemId,
          // A MERGED row reports the merged TOTAL, which is what the caller's
          // stepper must show — not the delta this call contributed.
          quantity: res.quantityWanted || (item && item.quantityWanted) || 1,
          merged: res.status === 'merged' || !!res.merged,
          skipped: res.status === 'skipped' || !!res.skipped,
          status: res.status || (res.skipped ? 'skipped' : (res.merged ? 'merged' : 'inserted')),
        };
      });
      if (addedCount) renderRail();
      if (addedCount && !failedCount) {
        BR.showToast('Added ' + addedCount + ' item' + (addedCount === 1 ? '' : 's'));
      } else if (addedCount) {
        BR.showToast('Added ' + addedCount + ' — ' + failedCount + (failedCount === 1 ? " item couldn't be added" : " items couldn't be added"));
      } else {
        BR.showToast("Couldn't add the bundle — please try again.");
      }
      if (onDone) onDone({ ok: addedCount > 0, addedCount: addedCount, failedCount: failedCount, results: outcomes });
    });
  }
  window.BirchRegistryStart.addBundleItems = addBundleItems;

  /** One candidate card per visible row in a bundle step's panel: the first
   *  card in that row that isn't already on the registry. A row that's empty,
   *  hidden (filtered out by the plan's subs — see showStep), or fully
   *  already-added contributes nothing, so "Add all" only ever adds what's
   *  genuinely new. */
  function bundleCandidates(panel) {
    var picks = [];
    $all('[data-rst-row]', panel).forEach(function (row) {
      if (row.hidden) return;
      var cards = $all('.rst-pcard', row);
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var slot = card.querySelector('[data-rst-action]');
        if (!slot || slot.dataset.rstItemId) continue; // already added
        picks.push({ card: card, slot: slot });
        break;
      }
    });
    return picks;
  }

  function handleBundleAdd(btn) {
    if (!btn || btn.disabled || !state.registry) return;
    var panel = btn.closest('[data-rst-step]');
    if (!panel) return;
    var picks = bundleCandidates(panel);
    if (!picks.length) {
      BR.showToast('Everything here is already on your registry.');
      return;
    }
    btn.disabled = true;
    picks.forEach(function (p) { setSlotBusy(p.slot, true); });
    var originalLabel = btn.textContent;
    btn.textContent = 'Adding…';
    addBundle(picks.map(function (p) { return cardPayload(p.card); }), function (data) {
      btn.disabled = false;
      btn.textContent = originalLabel;
      picks.forEach(function (p) { setSlotBusy(p.slot, false); });
      var results = data && data.ok && Array.isArray(data.results) ? data.results : null;
      if (!results) {
        BR.showToast("Couldn't add the bundle — please try again.");
        return;
      }
      var addedCount = 0;
      var failedCount = 0;
      results.forEach(function (res, i) {
        var pick = picks[i];
        if (!pick) return;
        if (res && res.item) {
          recordAddedItem(pick.card, pick.slot, res.item);
          addedCount++;
        } else {
          failedCount++;
        }
      });
      // One refresh for the whole batch — same calls handleAdd makes after a
      // single add, just called once instead of once per item.
      renderRail();
      if (addedCount && !failedCount) {
        BR.showToast('Added ' + addedCount + ' item' + (addedCount === 1 ? '' : 's'));
      } else if (addedCount) {
        BR.showToast('Added ' + addedCount + ' — ' + failedCount + (failedCount === 1 ? " item couldn't be added" : " items couldn't be added"));
      } else {
        BR.showToast("Couldn't add the bundle — please try again.");
      }
    });
  }

  function handleQty(slot, delta) {
    var itemId = slot.dataset.rstItemId;
    var card = slot.closest('.rst-pcard');
    if (!itemId || !state.registry) return;
    var meta = state.added[itemId] || { stepKey: (currentStep() || {}).key || 'other', qty: 1 };
    var next = (meta.qty || 1) + delta;
    setSlotBusy(slot, true);
    if (next < 1) {
      BR.API.removeItem(state.registry.id, itemId)
        .then(function () {
          // Shared with the guided flow's remove (applyRemovedItem above) so
          // the two can't drift: same mirror drop, same fingerprint clear,
          // same counters. The card's own identity is passed through because
          // this path has one — it's the same four fields the mirrored item
          // carries, just read off the DOM that's already in hand.
          applyRemovedItem(itemId, card ? cardIdentity(card) : null);
          renderRail();
          BR.showToast('Removed');
        })
        .catch(function () {
          setSlotBusy(slot, false);
          BR.showToast("Couldn't remove that — please try again.");
        });
      return;
    }
    if (next > MAX_ITEM_QTY) { setSlotBusy(slot, false); return; }
    BR.API.updateItem(state.registry.id, itemId, { quantityWanted: next })
      .then(function () {
        applyItemQuantity(itemId, next);
      })
      .catch(function () {
        setSlotBusy(slot, false);
        BR.showToast("Couldn't update the quantity — please try again.");
      });
  }

  function bumpStepCount(stepKey, delta) {
    var p = state.onboarding.progress || {};
    var counts = p.addedByStep || {};
    counts[stepKey] = Math.max(0, (counts[stepKey] || 0) + delta);
    persist({ progress: { addedByStep: counts } });
  }

  /** A resumed session lands on cards that are already on the registry — paint
   *  their steppers so the state on screen matches the state on the server. */
  function paintExistingItems() {
    var items = ((state.registry && state.registry.items) || []).filter(function (it) { return it.status !== 'removed'; });
    if (!items.length) return;
    var byShopify = Object.create(null);
    var byUrl = Object.create(null);
    items.forEach(function (it) {
      state.added[it.id] = { stepKey: state.added[it.id] ? state.added[it.id].stepKey : 'other', qty: it.quantityWanted || 1 };
      rememberAdded({ externalUrl: it.externalUrl, lushlifeProductId: it.lushlifeProductId, shopifyProductId: it.shopifyProductId, title: it.title }, true);
      if (it.shopifyProductId) byShopify[String(it.shopifyProductId)] = it;
      var u = normFeedUrl(it.externalUrl);
      if (u) byUrl[u] = it;
    });
    $all('.rst-pcard').forEach(function (card) {
      var slot = card.querySelector('[data-rst-action]');
      if (!slot || slot.dataset.rstItemId) return;
      var hit = card.dataset.external === '1'
        ? byUrl[normFeedUrl(card.dataset.externalUrl)]
        : byShopify[String(card.dataset.productId)];
      if (hit) renderAction(slot, { itemId: hit.id, qty: hit.quantityWanted || 1 });
    });
  }

  // ── product sheet (variant picker) ───────────────────────────────────────
  /** Keep the local mirror of the registry's items in step with a server write,
   *  so the sheet's Add⇄Remove state (which matches on variant id) is honest
   *  without a refetch. */
  function upsertLocalItem(item) {
    if (!item || !item.id) return;
    if (!state.registry) return;
    var items = state.registry.items = state.registry.items || [];
    var i = items.findIndex(function (it) { return it.id === item.id; });
    if (i >= 0) items[i] = Object.assign({}, items[i], item);
    else items.push(item);
  }
  /** Patch an item already in the local mirror. Unlike upsertLocalItem this
   *  never invents a row — a partial patch has no variant ids, and a stub in the
   *  mirror would be dead weight the picker can never match. */
  function patchLocalItem(itemId, patch) {
    if (!state.registry || !Array.isArray(state.registry.items)) return;
    var i = state.registry.items.findIndex(function (it) { return it.id === itemId; });
    if (i >= 0) state.registry.items[i] = Object.assign({}, state.registry.items[i], patch);
  }
  function dropLocalItem(itemId) {
    if (!state.registry || !Array.isArray(state.registry.items)) return;
    state.registry.items = state.registry.items.filter(function (it) { return it.id !== itemId; });
  }
  /** The local mirror's copy of one item, if we hold it. The read side of
   *  upsertLocalItem — applyRemovedItem derives the feed-dedupe identity from
   *  this so a removal can undo the exact fingerprint the add wrote, with no
   *  card on screen to read it off. */
  function localItemById(itemId) {
    if (!state.registry || !Array.isArray(state.registry.items)) return null;
    return state.registry.items.filter(function (it) { return it.id === itemId; })[0] || null;
  }

  /** The action slot currently bound to a registry item, if it's on screen. */
  function slotForItem(itemId) {
    return document.querySelector('[data-rst-action][data-rst-item-id="' + String(itemId).replace(/"/g, '\\"') + '"]');
  }
  /** ALL of them. The same product can sit in more than one row (a category
   *  row and an alternates row, say), and every slot bound to an item id is
   *  claiming that item's state — so a remove/qty write has to repaint all of
   *  them, not just whichever one querySelector happened to find first. */
  function slotsForItem(itemId) {
    return Array.prototype.slice.call(
      document.querySelectorAll('[data-rst-action][data-rst-item-id="' + String(itemId).replace(/"/g, '\\"') + '"]')
    );
  }

  /** Registry wiring for BirchDetailPopup, scoped to one card. Adding a
   *  DIFFERENT option from the sheet creates a second registry item (the server
   *  dedupes by exact variant id) — so the counts move even when this card's own
   *  stepper is already bound to another option. */
  function detailHooks(card) {
    var slot = card.querySelector('[data-rst-action]');
    return {
      registryId: state.registry ? state.registry.id : null,
      anchorItemId: (slot && slot.dataset.rstItemId) || null,
      getItems: function () { return (state.registry && state.registry.items) || []; },
      // CUR-213 — the sheet's Add (a product with one option) sends exactly
      // what this card's "+ Add" sends: the variant the tile shows.
      cardPayload: function () { return cardPayload(card); },
      onAdded: function (item, _payload, _merged) {
        if (!item) return;
        upsertLocalItem(item);
        var stepKey = (currentStep() || {}).key || 'other';
        var qty = item.quantityWanted || 1;
        if (!state.added[item.id]) {
          state.added[item.id] = { stepKey: stepKey, qty: qty };
          state.addedCount++;
          bumpStepCount(stepKey, 1);
        } else {
          state.added[item.id].qty = qty;
        }
        rememberAdded(cardIdentity(card), true);
        // Bind this card's stepper to the new item only when it isn't already
        // showing one — a second option added from the sheet gets its own row on
        // the registry, not a hijack of this card's control.
        if (slot && !slot.dataset.rstItemId) renderAction(slot, { itemId: item.id, qty: qty });
        renderRail();
      },
      onRemoved: function (itemId) {
        // Same shared bookkeeping as handleQty's step-to-zero and the guided
        // flow's remove (applyRemovedItem). The identity it clears used to be
        // read off the bound card, which meant a removal with no card on
        // screen left the feed-dedupe fingerprint set forever; the mirrored
        // item carries the same fields, so it is cleared either way now.
        var bound = slotForItem(itemId);
        var boundCard = bound && bound.closest('.rst-pcard');
        applyRemovedItem(itemId, boundCard ? cardIdentity(boundCard) : null);
        renderRail();
      },
      onConfirmed: function (item) { upsertLocalItem(item); },
    };
  }

  // ── step navigation ──────────────────────────────────────────────────────
  function markStep(key, listName) {
    var p = state.onboarding.progress || {};
    var done = (p.done || []).slice();
    var skipped = (p.skipped || []).slice();
    [done, skipped].forEach(function (l) {
      var i = l.indexOf(key);
      if (i !== -1) l.splice(i, 1);
    });
    (listName === 'done' ? done : skipped).push(key);
    persist({ progress: { done: done, skipped: skipped } });
  }

  // pausesShown entries are "<stepKey>:<kind>" (kind = 'reflect' | 'interlude'),
  // not bare step keys — Nursery and Parent-Recovery each have BOTH a reflect
  // and an interlude pending, and each needs its own "already shown" mark so
  // showing one doesn't suppress the other (CUR-80 final-review Finding I7).
  function pauseShownKey(stepKey, kind) { return stepKey + ':' + kind; }

  // Every pause (reflect and/or interlude, from guided-registry-moments.js)
  // that hasn't been shown for this step yet — in narrative order (reflect,
  // then interlude), never just the first kind found. A step can carry both
  // at once (Nursery: "where will you be at 2am" then "safe sleep, in six
  // lines"); returning only one meant the interlude — genuine safety
  // content — could never show for those steps.
  function pendingPauses(afterStepKey) {
    if (!window.BirchGuidedMoments) return [];
    var shown = (state.onboarding.progress || {}).pausesShown || [];
    var out = [];
    var reflect = window.BirchGuidedMoments.reflectAfter(afterStepKey);
    if (reflect && shown.indexOf(pauseShownKey(afterStepKey, 'reflect')) === -1) {
      out.push({ kind: 'reflect', key: afterStepKey, data: reflect });
    }
    var interlude = window.BirchGuidedMoments.interludeAfter(afterStepKey);
    if (interlude && shown.indexOf(pauseShownKey(afterStepKey, 'interlude')) === -1) {
      out.push({ kind: 'interlude', key: afterStepKey, data: interlude });
    }
    return out;
  }

  // §11 — one forward control. Moving on having added nothing IS the skip; the
  // rail records which of the two it was, and only ticks the productive one.
  function nextStep() {
    var step = currentStep();
    if (step) markStep(step.key, addedInStep(step.key) > 0 ? 'done' : 'skipped');
    pushHistory();
    var pauses = step ? pendingPauses(step.key) : [];
    if (pauses.length) {
      var targetIndex = state.stepIndex + 1;
      showScreen('guided-moment', { silent: true });
      // Chain every pending pause for this step, one Continue tap apiece,
      // before landing on the next step — see pendingPauses' doc comment.
      var runPause = function (i) {
        var pause = pauses[i];
        var shown = (state.onboarding.progress || {}).pausesShown || [];
        persist({ progress: { pausesShown: shown.concat(pauseShownKey(pause.key, pause.kind)) } });
        window.BirchGuidedMoments.renderPause(pause, function () {
          if (i + 1 < pauses.length) { runPause(i + 1); return; }
          showScreen('build', { silent: true });
          showStep(targetIndex);
        });
      };
      runPause(0);
      return;
    }
    showStep(state.stepIndex + 1);
  }

  function finish() {
    var counts = (state.onboarding.progress || {}).addedByStep || {};
    var total = 0;
    var cats = 0;
    Object.keys(counts).forEach(function (k) { if (counts[k] > 0) { total += counts[k]; cats++; } });
    $('[data-rst-done-heading]').textContent = total > 0
      ? total + ' item' + (total === 1 ? '' : 's') + ' across ' + cats + ' categor' + (cats === 1 ? 'y' : 'ies') + ". Here's your registry."
      : "Your registry is ready.";
    $('[data-rst-done-sub]').textContent = total > 0
      ? 'Everything you added is already saved. You can keep building, share it, or hand the rest to Birch.'
      : "You skipped the picks for now — your registry is set up and ready to fill.";
    var list = $('[data-rst-done-counts]');
    if (list && state.plan) {
      list.innerHTML = state.plan.steps
        .filter(function (s) { return counts[s.key] > 0; })
        .map(function (s) { return '<li>' + esc(s.label) + ' · ' + counts[s.key] + '</li>'; })
        .join('');
    }
    completeAndStamp('finished');
    $('[data-rst-step-actions]').hidden = true;
    $('[data-rst-mobile-actions]').hidden = true;
    showScreen('done');
  }

  function completeAndStamp(via) {
    persist({ completedAt: new Date().toISOString(), completedVia: via });
    try { localStorage.setItem(ONBOARDED_KEY, '1'); } catch (_) {}
  }

  // Shared onComplete/onSaveLater options object for both
  // window.BirchGuidedRegistry.init() call sites (route()'s resume path and
  // chooseGuided()) — Task 7. Publish and the guided flow simply running out
  // of screens both funnel through the SAME onComplete path renderSummary's
  // own "Continue" nav button also uses (guided-registry-screens.js's
  // advance()): stamp completion the same way the old walkthrough's finish()
  // does, then land in the registry editor, matching how the (retired) old
  // walkthrough's own "Open my registry" affordance navigates (goToEditor,
  // above — same URL construction, no history entry to unwind). Save-later
  // deliberately does NOT stamp completion: `completedAt`/`completedVia`
  // unset means a later visit's route() resumes back into the guided
  // machine (see the `ob.path === 'guided'` branch below) instead of
  // treating the registry as done.
  function guidedCompletionHandlers() {
    return {
      onComplete: function () { completeAndStamp('finished'); goToEditor(); },
      onSaveLater: function () { goToEditor(); },
      // Where the shopper was, for the guided machine's init() to resume onto
      // (UI audit CRITICAL 1 — a reload used to drop them back on the recap).
      // Two sources, most specific first:
      //   * `gscreen` on the URL, which is the guided history entry the browser
      //     is actually sitting on. A reload of a mid-flow screen restores that
      //     entry, so this is the exact screen that was showing.
      //   * `progress.guidedScreen`, the durable pointer the machine writes on
      //     every screen change. It is an EXISTING key on birch-registry's
      //     closed onboarding allow-list (registryOnboarding.js PROGRESS_KEYS,
      //     a capped 40-char string) — nothing new was invented for this, since
      //     an unknown key 400s the whole coalesced patch. This is what carries
      //     the resume across a fresh arrival with no `gscreen` on the URL.
      // The machine still refuses to honour either past an unanswered setup
      // fork — see its init().
      guidedScreen: guidedResumeScreen(),
      // CUR-151 — reopened from the editor: the flow opens its whole step bar.
      reopen: isReopen(),
      // CUR-151 — the recap's "Your setup" rows, and the way into one setup
      // page from its Change (see editSetup/returnToFlow).
      setupRows: function () {
        var edit = window.BirchGuidedSetupEdit;
        return edit ? edit.setupRows(state.onboarding.answers || {}, setupCfg()) : [];
      },
      editSetup: editSetup,
      // CUR-156 — the guided flow asks the setup questions itself.
      setup: { config: guidedSetupConfig, set: setGuidedSetupAnswer },
      // Re-fetch (and save) the plan once the setup answers it was fetched
      // under have changed. The recap calls this: setup is over by then.
      refreshPlan: refreshPlanIfStale,
      // CUR-157 — a published registry re-running the flow finishes with
      // "Done", not "Review & Publish".
      published: !!(state.registry && state.registry.status === 'published'),
    };
  }

  /** CUR-156 — what the guided setup screens need to paint THIS journey's
   *  questions: its own copy (the same JOURNEY config the pages read) and the
   *  "what you already have" list. `edit` is CUR-151's cfg, for nextEditPage. */
  function guidedSetupConfig() {
    var f = flow();
    var a = state.onboarding.answers || {};
    var hasTiming = pages().indexOf('2') !== -1;
    var n = babyCount();
    return {
      askDate: hasTiming && !f.noDate,
      askAge: hasTiming && !!f.age,
      timingHeading: hasTiming ? f.dateHeading : 'Anything else we should plan for?',
      timingSub: hasTiming ? (f.dateSub || '') : '',
      dateLabel: f.dateLabel || 'Estimated due date',
      dateMin: f.dateFuture ? todayISO() : '',
      ageLabel: f.ageHeading || 'Age',
      multiplesLabel: f.multiples || 'More than one is on the way',
      firstHeading: f.firstHeading || 'Is this your first baby?',
      bigTicket: BIG_TICKET.map(function (b) { return { key: b.key, label: b.label }; }),
      ownNote: a.multiples === true
        ? 'With ' + (MULTIPLES_NUMERAL[n] || 'more than one') + ' on the way we\u2019ve left the crib, bassinet and stroller in \u2014 '
          + 'another sleep surface and a ' + (n > 2 ? 'multi-seat' : 'double') + ' stroller are usually a genuine second purchase.'
        : '',
      edit: setupCfg(),
    };
  }

  /** CUR-156 — one setup answer from a guided screen, through the SAME write
   *  the pages use for it, so every side effect has one implementation. */
  function setGuidedSetupAnswer(field, value) {
    switch (field) {
      case 'journey':
      case 'ageRange':
        setChip(field, String(value), true);
        break;
      case 'firstBaby':
        setChip('firstBaby', value === true || value === 'true' ? 'true' : 'false', true);
        // "No" reveals what they already have; its defaults are a real
        // answer from the start, as when the own page opens.
        if ((state.onboarding.answers || {}).firstBaby === false) seedOwnAnswer();
        break;
      case 'multiples':
        setCheck('multiples', value === true);
        break;
      case 'multiplesCount':
        setSelect('multiplesCount', String(value));
        break;
      case 'arrivalDate':
        commitDate(value ? String(value) : '', { syncRegistry: true });
        break;
      case 'own':
        setOwnKey(String(value));
        break;
      default:
        return false;
    }
    return true;
  }

  function refreshPlanIfStale() {
    if (state.plan && state.planAnswersKey === JSON.stringify(planAnswers())) return;
    fetchPlan().then(function (plan) { if (plan) persist({ plan: plan }); });
  }

  function guidedResumeScreen() {
    var fromUrl;
    try { fromUrl = new URLSearchParams(location.search || '').get('gscreen'); } catch (_) { /* no URL access */ }
    // CUR-151 — reopened from the editor: land on the recap (the hub), unless
    // the URL already names a screen (a reload mid-visit stays put).
    return fromUrl || (isReopen() ? 'recap' : null) || (state.onboarding.progress || {}).guidedScreen || null;
  }

  /** CUR-151 — opened from the editor's "Guided setup" chip. Kept on the URL
   *  for the whole visit (showScreen and the flow's history both preserve other
   *  params), so a reload mid-flow does not bounce a finished registry. */
  function isReopen() {
    try { return new URLSearchParams(location.search || '').get('reopen') === '1'; } catch (_) { return false; }
  }

  function goToEditor(reason) {
    var step = currentStep();
    if (reason === 'exit') {
      persist({
        completedAt: new Date().toISOString(),
        completedVia: 'exit',
        progress: { currentStep: step ? step.key : null },
      });
      try { localStorage.setItem(ONBOARDED_KEY, '1'); } catch (_) {}
    }
    var p = flushPersist(true) || Promise.resolve();
    var id = state.registry ? state.registry.id : null;
    var url = '/pages/registry-edit' + (id ? '?id=' + encodeURIComponent(id) : '');
    // No confirm dialog — the action is non-destructive.
    Promise.resolve(p).then(function () { location.assign(url); });
    setTimeout(function () { location.assign(url); }, 400);
  }

  // ── the big-ticket deferral, inside the walkthrough (§2.3) ───────────────
  // The question itself is asked up front, on the `own` page. What lives here
  // is only the UNDO: the way back to something the answer removed, offered in
  // the category it was removed from.

  /** The BIG_TICKET entries this step lost to the deferral — i.e. the ones this
   *  panel can render a row for, that the plan's subs no longer list. */
  function deferredHere(panel, step) {
    var a = state.onboarding.answers || {};
    var cat = null;
    for (var i = 0; i < CATEGORIES.length; i++) if (CATEGORIES[i].key === step.key) { cat = CATEGORIES[i]; break; }
    if (!cat) return [];
    var eligible = eligibleSubs(cat, a);
    return deferredFor(a).filter(function (b) {
      if (eligible.indexOf(b.sub) === -1) return false;   // this step never offered it, or a fit rule took it
      if (step.subs.indexOf(b.sub) !== -1) return false;  // still in the plan (restored, or never deferred here)
      if (!panel.querySelector('[data-rst-row][data-feed-sub="' + b.sub + '"]')) return false; // no row to show
      return true;
    });
  }

  /** "Show cribs anyway" / "Actually, show me anyway" — reveal the deferred rows
   *  in this step and record that the family wants them, so the answer, the
   *  plan on the next fetch and the AI builder all say the same thing. */
  function restoreDeferred(panel) {
    var step = currentStep();
    if (!panel || !step) return;
    var lost = deferredHere(panel, step);
    var a = state.onboarding.answers = state.onboarding.answers || {};
    if (lost.length) {
      var wanted = Array.isArray(a.bigTicketWanted) ? a.bigTicketWanted.slice() : [];
      lost.forEach(function (b) { if (wanted.indexOf(b.key) === -1) wanted.push(b.key); });
      a.bigTicketWanted = wanted;
      persist({ answers: { bigTicketWanted: wanted } });
      // Keep the in-memory plan honest too: the rows are visible now, so the
      // step's subs must say so or the next showStep would hide them again.
      lost.forEach(function (b) { if (step.subs.indexOf(b.sub) === -1) step.subs.push(b.sub); });
      persist({ plan: state.plan });
      // The answer changed, so the cached plan no longer matches it. Clearing
      // the key makes the next fetchPlan re-plan instead of serving this one.
      state.planAnswersKey = null;
    }
    $all('[data-rst-row]', panel).forEach(function (r) { r.hidden = r.hasAttribute('data-feed-empty'); observeRow(r); });
    var empty = $('[data-rst-empty]', panel);
    if (empty) empty.hidden = true;
    var restore = $('[data-rst-restore]', panel);
    if (restore) restore.hidden = true;
    renderRail();
  }

  function currentRegistryId() {
    return state.registry && state.registry.id ? state.registry.id : null;
  }

  // ── boot ─────────────────────────────────────────────────────────────────
  function parseOnboarding(reg) {
    var raw = reg && reg.onboarding;
    if (!raw) return { v: 1, answers: {} };
    if (typeof raw === 'object') return raw;
    try {
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : { v: 1, answers: {} };
    } catch (_) { return { v: 1, answers: {} }; }
  }

  // CUR-167: "+ New registry" (?new=1) mints a fresh row, never reusing an
  // existing or empty one (no reuseEmpty), then pins ?id= in place of ?new=1
  // so a refresh reopens it instead of minting another. A guest's create
  // always returns their one registry (the server caps guests at one), so a
  // guest reaching here just continues with theirs.
  //
  // CUR-179 — a failed create REJECTS with NEW_REGISTRY_FAILED. It used to
  // resolve null, and resolveRegistry then fell back to the most recently
  // updated registry, so the answers and picks meant for the new one landed
  // in the parent's existing registry.
  function createNewRegistry() {
    return BR.API.create({})
      .then(function (d) {
        var reg = d && d.registry;
        if (!reg || !reg.id) throw new Error(NEW_REGISTRY_FAILED);
        try {
          var u = new URL(location.href);
          u.searchParams.delete('new');
          u.searchParams.set('id', reg.id);
          history.replaceState(null, '', u.toString());
        } catch (_) {}
        return reg;
      }, function () { throw new Error(NEW_REGISTRY_FAILED); });
  }

  /** CUR-179 — the build choice becomes an honest failure with a retry. The
   *  URL still says ?new=1, so a reload asks for a new registry again. */
  function showNewRegistryError() {
    var screen = root.querySelector('[data-rst-screen="path"]');
    if (!screen) return;
    var heading = screen.querySelector('.rst-heading');
    var sub = screen.querySelector('[data-rst-fork-sub]');
    var choose = screen.querySelector('.rst-choose');
    if (heading) heading.textContent = 'We couldn’t start your new registry';
    if (sub) sub.textContent = 'Your other registries haven’t changed.';
    if (choose) {
      choose.innerHTML = '<button type="button" class="rst-primary" data-rst-new-retry>Try again</button>';
      var retry = choose.querySelector('[data-rst-new-retry]');
      if (retry) retry.addEventListener('click', function () { location.reload(); });
    }
    if (heading && heading.focus) heading.focus();
  }

  function resolveRegistry() {
    var params = new URLSearchParams(location.search || '');
    var id = params.get('id');
    var chain = id
      ? BR.API.get(id).then(function (d) { return d && d.registry; }).catch(function () { return null; })
      : params.get('new') === '1'
        ? createNewRegistry()
        : Promise.resolve(null);
    return chain
      .then(function (reg) {
        if (reg) return reg;
        return BR.API.list()
          .then(function (d) {
            var active = ((d && d.registries) || [])
              .filter(function (r) { return r.status !== 'ended'; })
              .sort(function (x, y) { return new Date(y.updatedAt) - new Date(x.updatedAt); });
            return active[0] || null;
          })
          .catch(function () { return null; });
      })
      .then(function (reg) {
        if (reg) return reg;
        // Mirrors registry-unified's eagerCreateRegistry: the guest session is
        // minted lazily inside BirchRegistry.request(), no extra call here.
        return BR.API.create({ reuseEmpty: true }).then(function (d) { return d && d.registry; });
      })
      .then(function (reg) {
        if (!reg) throw new Error('no_registry');
        state.registry = reg;
        // Full row (items + onboarding) when list gave us a summary.
        if (!Array.isArray(reg.items)) {
          return BR.API.get(reg.id).then(function (d) {
            state.registry = (d && d.registry) || reg;
            return state.registry;
          }).catch(function () { return reg; });
        }
        return reg;
      });
  }

  // Page 1 is answerable from first paint: the click listener is bound at
  // script eval, while the registry row is still ~600ms away. Anything answered
  // in that window lives only in `state.onboarding`, and the row that lands
  // afterwards is OLDER than it — so replacing state with it wholesale rewrites
  // the person's own answer to the default UNDERNEATH them, mid-page. Tapping
  // "Adopting" fast enough put them on the age question and then flipped it
  // back to "When are they arriving?" (with the due-date field) a third of a
  // second later. Local wins, and is written back so the row catches up.
  function carryPreBootAnswers(preBoot, server) {
    var pre = (preBoot && preBoot.answers) || {};
    var carried = null;
    Object.keys(pre).forEach(function (k) {
      if (pre[k] == null) return;
      (server.answers = server.answers || {})[k] = pre[k];
      (carried = carried || {})[k] = pre[k];
    });
    return carried;
  }

  function route() {
    var reg = state.registry;
    var preBoot = state.onboarding;
    state.onboarding = parseOnboarding(reg);
    var carried = carryPreBootAnswers(preBoot, state.onboarding);
    if (!state.onboarding.startedAt) persist({ v: 1, startedAt: new Date().toISOString() });
    var items = (reg.items || []).filter(function (it) { return it.status !== 'removed'; });
    var ob = state.onboarding;
    // Never re-ask, and never overwrite real work: a completed intake or a
    // populated registry that predates the intake both go straight to the editor.
    // CUR-151 — except when reopened from the editor's "Guided setup" chip:
    // then every registry, finished or built elsewhere, comes back in.
    if (!isReopen() && (ob.completedAt || (items.length > 0 && !ob.path))) {
      try { localStorage.setItem(ONBOARDED_KEY, '1'); } catch (_) {}
      location.replace('/pages/registry-edit?id=' + encodeURIComponent(reg.id));
      return;
    }
    state.addedCount = items.length;
    // Answered before the row arrived (see carryPreBootAnswers) — the server
    // has never seen these, so they are written back now.
    if (carried) persist({ answers: carried });

    // Seed the default journey when this session has never answered it, so the
    // pre-marked tile is backed by a real stored answer the moment Continue is
    // pressed. A resumed session keeps whatever it chose.
    var ans = ob.answers = ob.answers || {};
    if (ans.journey == null) {
      ans.journey = DEFAULT_JOURNEY;
      persist({ answers: { journey: DEFAULT_JOURNEY } });
    }
    // A session saved before this journey stopped asking for a date (or before
    // the question was retired for it) must not carry that date forward: it
    // would still reach the planner and the AI brief as a due date nobody was
    // asked for. Same rule as the journey switch in setChip().
    if ((pages().indexOf('2') === -1 || flow().noDate) && ans.arrivalDate != null) {
      delete ans.arrivalDate;
      persist({ answers: { arrivalDate: null } });
    }
    // Mirror of the same rule for the age band: only adopting and "baby's here"
    // ask it, and a value carried in from any other journey would reach the
    // planner as an age the child will not be.
    if (!flow().age && ans.ageRange != null) {
      delete ans.ageRange;
      persist({ answers: { ageRange: null } });
    }

    // Restore chips/checkbox/date from saved answers.
    ['journey', 'ageRange', 'firstBaby'].forEach(reflectChips);
    reflectChecks('multiples');
    reflectMultiples();
    state.priorityOrder = ((ob.answers || {}).priorities || []).slice();
    applyJourneyCopy();
    var dateInput = $('[data-rst-date]');
    if (dateInput && (ob.answers || {}).arrivalDate) dateInput.value = ob.answers.arrivalDate;
    syncDateField();
    // CUR-151 — the registry's due date is edited in Settings after setup; on
    // re-entry it is the newer truth, so the answers (and the recap) follow it.
    if (isReopen() && window.BirchGuidedSetupEdit) {
      var synced = window.BirchGuidedSetupEdit.arrivalFromRegistry(
        reg.dueDate ? new Date(reg.dueDate).toISOString() : null, ans, setupCfg(), todayISO());
      if (synced) {
        ans.arrivalDate = synced;
        persist({ answers: { arrivalDate: synced } });
        if (dateInput) dateInput.value = synced;
        syncDateField();
      }
    }
    paintExistingItems();

    if (ob.path === 'guided' && ob.plan && Array.isArray(ob.plan.steps)) {
      // A plan saved by an older build can name a step this page no longer
      // renders (the retired synthetic "The big stuff" step is the reason this
      // guard exists). Showing it would hide every panel and leave a blank
      // stage, so unrenderable steps are dropped on the way in; if that empties
      // the plan, we re-plan from the answers instead of resuming a ghost.
      var panelKeys = {};
      // Read the keys off the DOM rather than interpolating a stored key into a
      // selector — a stored plan is validated for shape and length, not charset.
      $all('[data-rst-step]').forEach(function (el) { panelKeys[el.getAttribute('data-rst-step')] = 1; });
      var renderable = ob.plan.steps.filter(function (st) { return panelKeys[st.key] === 1; });
      if (renderable.length === 0) {
        if (isReopen()) { chooseGuided(); return; }
        showScreen('path');
        fetchPlan();
        return;
      }
      state.plan = { id: ob.plan.id, generatedAt: ob.plan.generatedAt, source: ob.plan.source, steps: renderable };
      state.planAnswersKey = JSON.stringify(planAnswers());

      // chooseGuided() persists { path:'guided', plan } and flushes BEFORE the
      // setup forks even run (they run client-side, over several small
      // persist() calls, one per tap). A reload/return mid-fork used to land
      // straight past them via a `showStep()` fallthrough that used to live
      // here — permanently skipping whatever forks hadn't been answered yet,
      // silently (CUR-80 final-review Finding I8). Always resume INTO the
      // guided machine instead: it now owns the ENTIRE guided path end to
      // end, setup forks through the summary screen's Publish/Save-later
      // (Task 7) — there is no separate "forks are done, skip straight to
      // showStep()" case left, since the flow's own summary (not this route)
      // is what hands off to the editor now. guided-registry-screens.js's
      // own init() already seeds pendingValue/index from `answers` (Task 2)
      // and lands on the recap once every setup fork has one, so a resume
      // mid-fork or at the recap just works by calling init() the same way
      // every time. NOT true of the moment screens after the recap (pick-one/
      // bundle/reflect/interlude): no progress key for those is persisted, so
      // init() only ever lands on a setup fork or the recap — never resumed
      // partway through a moment the person had already reached.
      pushHistory();
      // WHAT SITS UNDER THE FLOW'S FIRST HISTORY ENTRY (UI audit CRITICAL 2).
      // The guided machine pushes one entry per screen, so the entry the page
      // LOADED on becomes the floor of that trail — what a Back press from the
      // flow's first screen lands on. Arriving on a bare /pages/registry-start
      // (a bookmark, a shared link, "open my registry" from elsewhere) that
      // floor resolves to page 1 of the intake, and a shopper resumed deep into
      // the flow was dropped onto a question they answered weeks ago — observed
      // live. Rewriting it to the fork puts a real page of THIS flow there
      // instead: "How would you like to build it", which they can walk forward
      // from again (and "Walk me through it" resumes them where they left off,
      // since the screen id is persisted). replaceState, not push — it renames
      // the entry that is already there rather than adding one.
      //
      // Skipped when the page loaded ON one of the flow's own entries (a reload
      // mid-flow): the trail the shopper walked before the reload is already
      // underneath, and rewriting the top of it would throw that away.
      try {
        if (new URLSearchParams(location.search || '').get('step') !== 'guided-moment') {
          var floorUrl = new URL(location.href);
          floorUrl.searchParams.set('step', FORK);
          floorUrl.searchParams.delete('gscreen');
          if (state.registry) floorUrl.searchParams.set('id', state.registry.id);
          history.replaceState(null, '', floorUrl.toString());
        }
      } catch (_) { /* no history/URL access — the flow still works */ }
      showScreen('guided-moment', { silent: true });
      window.BirchGuidedRegistry.init(Object.assign({
        answers: ans,
        persist: persist,
      }, guidedCompletionHandlers()));
      return;
    }
    // CUR-151 — every registry can come back into the guided flow: one built in
    // the editor or by the AI path gets the guided path (and a plan) now.
    if (isReopen()) { chooseGuided(); return; }
    if (ob.path === 'ai') {
      // Legacy state: chose the since-removed AI path on an earlier visit and
      // came back here (a Back press, a stale bookmark). The editor is where
      // that path lives — but WITHOUT `aibuild`, so returning here can never
      // re-fire a build they already got. Only the retired launch flow parked
      // that payload, and the editor consumed it exactly once, long since.
      location.replace('/pages/registry-edit?id=' + encodeURIComponent(reg.id) + '&building=1');
      return;
    }
    var urlStep = new URLSearchParams(location.search || '').get('step');
    // A deep link starts with the pages it skipped already on the back stack,
    // so Back walks the questionnaire rather than dead-ending on arrival.
    // CUR-156 — the fork is the first page, so it is where a fresh visit lands
    // and the first entry behind a deep-linked setup page. Its plan (handed to
    // Build together) is warmed while the parent reads it.
    fetchPlan();
    if (urlStep && isQuestionPage(urlStep)) {
      var list = [FORK].concat(pages());
      // Only when nothing walked here already: a person who answered page 1
      // before the row arrived pushed their own entry on the way to page 2, and
      // seeding on top of it would make Back land on the same page twice.
      if (!state.history.length) {
        list.slice(0, list.indexOf(urlStep)).forEach(function (s) { state.history.push({ screen: s }); });
      }
      showScreen(urlStep);
    } else showScreen(FORK, { silent: true });
  }

  function chooseGuided() {
    var go = function (plan) {
      persist({ path: 'guided', plan: plan });
      flushPersist(false);
      pushHistory();
      showScreen('guided-moment', { silent: true });
      window.BirchGuidedRegistry.init(Object.assign({
        answers: state.onboarding.answers,
        persist: persist,
      }, guidedCompletionHandlers()));
    };
    if (state.plan) go(state.plan);
    else fetchPlan().then(go);
  }

  // ── §22.1 research confirm ───────────────────────────────────────────────
  // A research link opens in a NEW TAB. Mid-flow that reads as "my registry is
  // gone", so it's confirmed first. The dialog lives outside [data-rst-root]
  // (the .rst-page clip-path would crop a fixed child), hence its own listeners.
  //
  // The "yes" control is a real anchor whose href we rewrite — a scripted
  // window.open here would be one gesture removed from the click and can be
  // popup-blocked.
  var researchModal = document.querySelector('[data-rst-research-modal]');
  var researchReturnFocus = null;

  function closeResearchConfirm() {
    if (!researchModal || researchModal.hidden) return;
    researchModal.hidden = true;
    document.removeEventListener('keydown', onResearchKey);
    if (researchReturnFocus && document.contains(researchReturnFocus)) researchReturnFocus.focus();
    researchReturnFocus = null;
  }

  function onResearchKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeResearchConfirm(); }
  }

  function openResearchConfirm(link) {
    if (!researchModal) return false;
    var go = researchModal.querySelector('[data-rst-research-go]');
    var name = researchModal.querySelector('[data-rst-research-name]');
    if (!go) return false;
    go.setAttribute('href', link.getAttribute('href') || '/pages/research');
    if (name) name.textContent = link.getAttribute('data-rst-research-name') || 'the';
    researchReturnFocus = link;
    researchModal.hidden = false;
    document.addEventListener('keydown', onResearchKey);
    go.focus();
    return true;
  }

  if (researchModal) {
    researchModal.addEventListener('click', function (e) {
      if (e.target.closest('[data-rst-research-cancel]')) { e.preventDefault(); closeResearchConfirm(); }
      // Confirmed: the anchor's own target="_blank" does the opening, so we
      // only clean up behind it.
      else if (e.target.closest('[data-rst-research-go]')) closeResearchConfirm();
    });
  }

  // ── events ───────────────────────────────────────────────────────────────
  root.addEventListener('click', function (e) {
    // §22.1 — a research link asks before it takes the tab. If the dialog is
    // missing for any reason the link stays a plain link and just opens.
    var research = e.target.closest('[data-rst-research]');
    if (research) {
      if (openResearchConfirm(research)) e.preventDefault();
      return;
    }

    // §18 — the image and the title open the product sheet, which fetches the
    // full description/specs from /apps/registry/api/product-detail.
    var detail = e.target.closest('[data-rst-detail]');
    if (detail) {
      var dcard = detail.closest('.rst-pcard');
      if (dcard && window.BirchDetailPopup) {
        e.preventDefault();
        // Hooks turn on the sheet's variant picker (option pills + a single
        // Add/Remove/Confirm CTA) — the same one registry-edit shows. Without
        // them the sheet is detail-only, which is what this page used to be.
        window.BirchDetailPopup.openFromCard(dcard, detailHooks(dcard));
      }
      return;
    }

    var ownTile = e.target.closest('[data-rst-own]');
    if (ownTile) return setOwn(ownTile);

    var chip = e.target.closest('[data-rst-chip]');
    if (chip) {
      var advance = chip.hasAttribute('data-rst-advance');
      setChip(chip.getAttribute('data-field'), chip.getAttribute('data-value'), advance);
      // §7 — a choice IS the continue. Re-tapping the same tile re-confirms it
      // and moves on too, so the answer can never be silently un-set. The short
      // delay lets the tile's chosen state register before the panel changes;
      // a second tap in that window replaces the pending hop rather than
      // queueing a second one (which would double up the back stack).
      if (advance) {
        var from = state.screen;
        if (state.advanceTimer) clearTimeout(state.advanceTimer);
        state.advanceTimer = setTimeout(function () {
          state.advanceTimer = null;
          if (state.screen === from) advanceFrom(from);
        }, 220);
      }
      return;
    }


    if (e.target.closest('[data-rst-back]')) return state.editSetup ? returnToFlow() : goBack();

    var next = e.target.closest('[data-rst-next]');
    if (next) return advanceFrom(next.getAttribute('data-rst-next'));

    if (e.target.closest('[data-rst-skip-all]')) {
      // CUR-156 — Browse was chosen before these pages, so skipping the rest
      // goes where Browse ends.
      return finishBrowse();
    }

    var path = e.target.closest('[data-rst-path]');
    if (path) {
      // CUR-156 — the fork is on screen from first paint now, before boot has
      // resolved the registry; a choice made then has nothing to save to.
      if (!state.registry) return;
      var choice = path.getAttribute('data-rst-path');
      if (choice === 'guided') return chooseGuided();
      if (choice === 'browse') {
        // CUR-156 — the fork is asked first now: Browse answers the setup
        // pages next, and the last of them opens the editor (finishBrowse).
        pushHistory();
        return showScreen(pages()[0]);
      }
      return;
    }

    var qtyBtn = e.target.closest('[data-rst-qty-step]');
    if (qtyBtn) {
      var qslot = qtyBtn.closest('[data-rst-action]');
      if (qslot) handleQty(qslot, parseInt(qtyBtn.getAttribute('data-rst-qty-step'), 10) || 0);
      return;
    }

    var addBtn = e.target.closest('[data-rst-add]');
    if (addBtn) return handleAdd(addBtn);

    var bundleBtn = e.target.closest('[data-rst-bundle-add]');
    if (bundleBtn) return handleBundleAdd(bundleBtn);

    var goto = e.target.closest('[data-rst-goto]');
    if (goto && state.plan) {
      var idx = state.plan.steps.findIndex(function (s) { return s.key === goto.getAttribute('data-rst-goto'); });
      if (idx >= 0 && idx !== state.stepIndex) { pushHistory(); return showStep(idx); }
      if (idx >= 0) return;
    }

    if (e.target.closest('[data-rst-date-clear]')) {
      var dinput = $('[data-rst-date]');
      if (dinput) { dinput.value = ''; commitDate(''); }
      return;
    }
    // The whole date shell is the target — clicking anywhere on it (except the
    // clear button, handled above) opens the native picker.
    var shell = e.target.closest('[data-rst-date-shell]');
    if (shell) {
      var di = $('[data-rst-date]');
      if (di) {
        try { di.showPicker(); } catch (_) { di.focus(); }
      }
      return;
    }

    if (e.target.closest('[data-rst-next-step], [data-rst-next-step-mobile]')) return nextStep();
    if (e.target.closest('[data-rst-exit-cta], [data-rst-exit-cta-mobile]')) return goToEditor('exit');
    if (e.target.closest('[data-rst-open-registry]')) return goToEditor('done');
    if (e.target.closest('[data-rst-show-anyway], [data-rst-restore-btn]')) {
      return restoreDeferred(e.target.closest('[data-rst-step]'));
    }
    if (e.target.closest('[data-rst-mobile-steps]')) {
      var rail = $('[data-rst-rail-wrap]');
      if (rail) rail.style.display = rail.style.display === 'flex' ? '' : 'flex';
    }
  });

  root.addEventListener('change', function (e) {
    if (e.target.matches('[data-rst-date]')) return commitDate(e.target.value || '');
    if (e.target.matches('[data-rst-check]')) return setCheck(e.target.getAttribute('data-field'), e.target.checked);
    if (e.target.matches('[data-rst-select]')) return setSelect(e.target.getAttribute('data-field'), e.target.value);
  });

  // Arrow-key movement inside chip groups.
  root.addEventListener('keydown', function (e) {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key) === -1) return;
    var chip = e.target.closest('[data-rst-chip], [data-rst-own]');
    if (!chip) return;
    var group = chip.closest('.rst-chips');
    if (!group) return;
    var chips = $all('[data-rst-chip], [data-rst-own]', group);
    var i = chips.indexOf(chip);
    var nextChip = (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? chips[i - 1] : chips[i + 1];
    if (nextChip) { e.preventDefault(); nextChip.focus(); }
  });

  window.addEventListener('popstate', function (e) {
    var params = new URLSearchParams(location.search || '');
    var step = params.get('step') || FORK;
    // ── the guided flow's own entries (CUR-80, UI audit CRITICAL 2) ─────────
    // The guided machine pushes one entry per screen so Back walks the flow a
    // screen at a time instead of ejecting the shopper from it. Those entries
    // are told apart by the `birchGuided` MARKER ON history.state, never by the
    // URL alone: the old walkthrough writes `?step=build&cat=…` into the same
    // URL with replaceState, and the two must not be able to claim each other's
    // entries. This stays the page's only popstate handler — the guided module
    // adds none of its own — so there is exactly one owner of a traversal.
    //
    // Screen visibility stays here (showScreen), navigation stays there
    // (restore -> its goTo -> renderCurrent). canRestore is asked first so a
    // marked entry the machine cannot serve — a screen id this flow config no
    // longer authors, or a page where init() never ran — falls through to the
    // ordinary handling below instead of un-hiding an empty section.
    var st = e && e.state;
    var guided = window.BirchGuidedRegistry;
    if (st && st.birchGuided && guided && guided.canRestore && guided.canRestore(st.gscreen)) {
      showScreen('guided-moment', { silent: true });
      guided.restore(st.gscreen);
      return;
    }
    if (step === 'build' && state.plan) {
      var cat = params.get('cat');
      var idx = state.plan.steps.findIndex(function (s) { return s.key === cat; });
      showScreen('build', { silent: true });
      showStep(idx >= 0 ? idx : 0, { silent: true });
    } else if (step === FORK) {
      fetchPlan();
      showScreen(FORK, { silent: true });
    } else if (isQuestionPage(step)) {
      showScreen(step, { silent: true });
    }
  });

  function boot() {
    // The document is fully parsed now — anything that shifted the header since
    // the script-eval park gets corrected here, still without animation.
    parkFlow();
    fillStepIcons();
    renderBundleButtons();
    var claim = BR.isGuest() ? Promise.resolve([]) : Promise.resolve(BR.claimGuestWork());
    Promise.resolve(claim)
      .then(resolveRegistry)
      .then(route)
      .then(function () {
        // Last correction, then the flow is done with window scroll for good.
        parkFlow();
        state.parkDone = true;
      })
      .catch(function (err) {
        // CUR-179 — "+ New registry" couldn't be created: say so here. The
        // editor would open (or create) some other registry instead.
        if (err && err.message === NEW_REGISTRY_FAILED) { showNewRegistryError(); return; }
        // No identity / offline — the editor is the safe landing.
        location.replace('/pages/registry-edit?create=1');
      });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
